// src/host/CreateListing.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/useAuth";
import {
  publishListing,
  suggestDayRate,
  getHostVerificationStatus,
  submitIdAndSelfie,
  submitRightToList,
  geocodeListingAddressFn,
  placesAutocompleteFn,
} from "../firebase";

/** ------------------------------ Brand -------------------------------- **/
const BRAND_NAME = "You're Welcome";
const BRAND = "#7C9082";
const BRAND_HOVER = "#6C7F72";

/** ------------------------------ Pricing Policy ------------------------ **/
// Host enters *base* price. We show guests a fee-included price, rounded up, so you never lose margin.
const MARKUP_RATE = 0.15; // 15% baked into guest-facing display price
const HOST_FEE_RATE = 0.03; // 3% normal host fee

// Host promo: 0% host fee for first N months
const HOST_FEE_PROMO_ACTIVE = true;   // flip to false when promo ends
const HOST_FEE_PROMO_MONTHS = 6;
const HOST_FEE_RATE_EFFECTIVE = HOST_FEE_PROMO_ACTIVE ? 0 : HOST_FEE_RATE;
const ROUND_STEP_DAY = 1; // round up to nearest $1
const ROUND_STEP_NIGHT = 1; // round up to nearest $1

// Round up to the nearest "step" (e.g., $1, $5, etc.)
function ceilToStep(n, step = 1) {
  const x = Number(n || 0);
  const s = Number(step || 1);
  if (!isFinite(x) || !isFinite(s) || s <= 0) return 0;
  return Math.ceil(x / s) * s;
}
function computeGuestDisplay(base, step) {
  const b = Number(base || 0);
  if (!(b > 0)) return null;
  const raw = b * (1 + MARKUP_RATE);
  return ceilToStep(raw, step);
}
function computeHostPayoutEstimate(base, feeRate = HOST_FEE_RATE) {
  const b = Number(base || 0);
  const r = Number(feeRate || 0);
  if (!(b > 0)) return null;
  return b * (1 - r);
}

// Format helpers
function money0(n) {
  if (n == null || !isFinite(n)) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}
function money2(n) {
  if (n == null || !isFinite(n)) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/** ------------------------------ Cancellation Policy ------------------ **/
const DEFAULT_CANCELLATION_POLICY = {
  key: "standard_v1",
  title: "Standard cancellation",
  guestFacingText:
    "Most trips include a 48-hour grace period (as long as check-in is more than 48 hours away), extra flexibility for far-in-advance bookings made 60+ days before check-in, and no grace period on last-minute stays that start within 48 hours. After these windows, the stay price is non-refundable. Service fees are non-refundable except where required by law or under our Extenuating Circumstances policy.",
  guestFacingPoints: [
    "48-hour grace period on most bookings: cancel within 48 hours of booking and if check-in is still more than 48 hours away, you get a full refund of the stay price.",
    "If you booked less than 60 days before check-in (but more than 48 hours away), the stay price becomes non-refundable after this 48-hour grace period ends.",
    "If you booked 60 days or more before check-in, you can still cancel for a full refund until 30 days after booking, as long as your stay hasn't started. After that, the stay price is non-refundable.",
    "If you book a stay that starts within 48 hours, there is no grace period and the stay price is non-refundable from the moment you book.",
    "Service fees are non-refundable except where required by law or under our Extenuating Circumstances policy.",
  ],
};

/** ------------------------------ Config -------------------------------- **/
const DRAFT_KEY = "uh_draft_listing_v5";
const MAX_FILES = 20;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_MB = 15;
const IMG_MAX_DIM = 3200;
const IMG_QUALITY = 0.92;

const STEP_TITLES = [
  "ID verification",
  "Right to list",
  "Title & summary",
  "Space details",
  "Capacity & beds",
  "Location",
  "Amenities",
  "Neighbors & noise",
  "Photos",
  "Usage & pricing",
  "Things to know",
  "Review & preview",
];
const TOTAL_STEPS = STEP_TITLES.length;

const BOOKING_MODE_LABEL = {
  day: "Daytime only",
  overnight: "Overnight",
  hybrid: "Day + Overnight",
};

/** ----------------------- Space within a home -------------------------- **/
const SPACE_TYPES = [
  { value: "entire_place", label: "Entire place" },
  { value: "private_room", label: "Private room" },
  { value: "shared_room", label: "Shared room" },
  { value: "outdoor_space", label: "Outdoor area" },
  { value: "other", label: "Other" },
];

/** ----------------------- Property types ------------------------------ **/
const PROPERTY_TYPES = [
  { value: "house", label: "House" },
  { value: "apartment", label: "Apartment" },
  { value: "condo", label: "Condo" },
  { value: "loft", label: "Loft" },
  { value: "duplex", label: "Duplex" },
  { value: "townhome", label: "Townhome" },
  { value: "farm", label: "Farm" },
  { value: "event_hall", label: "Event hall" },
  { value: "warehouse", label: "Warehouse" },
  { value: "studio", label: "Studio" },
  { value: "cabin", label: "Cabin" },
  { value: "villa", label: "Villa" },
  { value: "other", label: "Other" },
];

/** ---------------------- Children suitability ------------------------- **/
const CHILDREN_OPTIONS = [
  { value: "all_ages", label: "All ages welcome" },
  { value: "2plus", label: "2+ years only (toddler-safe)" },
  { value: "no_kids", label: "Not suitable for children" },
];

/** ---------------------- Host presence -------------------------------- **/
const HOST_PRESENCE_OPTIONS = [
  { value: "yes", label: "Yes, I live onsite" },
  { value: "separate_unit", label: "Yes, in separate unit" },
  { value: "no", label: "No, I don't live onsite" },
];

/** ---------------------- Event types --------------------------------- **/
const EVENT_TYPES = [
  "Baby showers",
  "Dinners",
  "Corporate offsites",
  "Kids' birthdays",
  "Wedding events",
  "Graduation parties",
  "Bridal showers",
  "Family reunions",
  "Photo shoots",
  "Workshops",
  "Meetings",
  "Other gatherings",
];

const ENTRANCE_TYPES = [
  { value: "dedicated", label: "Dedicated entrance" },
  { value: "shared", label: "Shared entrance" },
];

const AREA_CHOICES = [
  "Kitchen access",
  "Living room",
  "Dining room",
  "Backyard / Patio",
  "Pool",
  "Rooftop / Deck",
  "Basement",
  "Garage",
  "Driveway / Parking",
  "Private bathroom",
  "Shared bathroom",
  "Workspace / Office",
];

/** --------------------------- Amenity Icons ---------------------------- **/
const ICON_STROKE = 1.75;
const IconBase = ({ children, selected, title }) => (
  <span
    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border text-neutral-700 transition"
    style={{
      borderColor: selected ? BRAND : "#e5e7eb",
      backgroundColor: selected ? "#f4f7f5" : "white",
    }}
    title={title}
    aria-hidden
  >
    {children}
  </span>
);

const Svg = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor">
    <g strokeWidth={ICON_STROKE} strokeLinecap="round" strokeLinejoin="round">
      {children}
    </g>
  </svg>
);

const ICONS = {
  wifi: ({ selected }) => (
    <IconBase selected={selected} title="Wi-Fi">
      <Svg>
        <path d="M2.5 8.5c5.5-5.5 13.5-5.5 19 0" />
        <path d="M5 11c4-4 10-4 14 0" />
        <path d="M8 13.5c2.5-2.5 6.5-2.5 9 0" />
        <circle cx="12.5" cy="17.5" r="1.5" />
      </Svg>
    </IconBase>
  ),
  tv: ({ selected }) => (
    <IconBase selected={selected} title="TV">
      <Svg>
        <rect x="3" y="6" width="18" height="11" rx="2" />
        <path d="M12 17v3" />
      </Svg>
    </IconBase>
  ),
  heating: ({ selected }) => (
    <IconBase selected={selected} title="Heating">
      <Svg>
        <path d="M12 5c2 2 2 4 0 6-2 2-1 5 2 7" />
        <path d="M9 13c-1.5 2 0 5 3 6" />
      </Svg>
    </IconBase>
  ),
  ac: ({ selected }) => (
    <IconBase selected={selected} title="Air conditioning">
      <Svg>
        <path d="M12 3v18" />
        <path d="M6 6l12 12" />
        <path d="M6 18l12-12" />
        <path d="M3 12h18" />
      </Svg>
    </IconBase>
  ),
  washer: ({ selected }) => (
    <IconBase selected={selected} title="Washer">
      <Svg>
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <circle cx="12" cy="13" r="4" />
        <path d="M7 6h3M12 6h3" />
      </Svg>
    </IconBase>
  ),
  dryer: ({ selected }) => (
    <IconBase selected={selected} title="Dryer">
      <Svg>
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <circle cx="12" cy="13" r="4" />
        <path d="M8.5 11.5c2 1.5 4.5 1.5 7 0" />
      </Svg>
    </IconBase>
  ),
  workspace: ({ selected }) => (
    <IconBase selected={selected} title="Dedicated workspace">
      <Svg>
        <rect x="4" y="6" width="16" height="9" rx="2" />
        <path d="M8 20h8M12 15v5" />
      </Svg>
    </IconBase>
  ),
  iron: ({ selected }) => (
    <IconBase selected={selected} title="Iron">
      <Svg>
        <path d="M5 15h14l-1-5a5 5 0 0 0-5-4H7" />
        <path d="M5 18h14" />
      </Svg>
    </IconBase>
  ),
  hair_dryer: ({ selected }) => (
    <IconBase selected={selected} title="Hair dryer">
      <Svg>
        <path d="M4 9h9a4 4 0 0 1 4 4v0H9a5 5 0 0 1-5-5v0z" />
        <path d="M17 13l3 2" />
        <path d="M8 13v5" />
      </Svg>
    </IconBase>
  ),
  kitchen: ({ selected }) => (
    <IconBase selected={selected} title="Kitchen">
      <Svg>
        <rect x="5" y="7" width="14" height="10" rx="2" />
        <path d="M9 7v-2M15 7v-2" />
      </Svg>
    </IconBase>
  ),
  refrigerator: ({ selected }) => (
    <IconBase selected={selected} title="Refrigerator">
      <Svg>
        <rect x="7" y="3" width="10" height="18" rx="2" />
        <path d="M7 10h10M10 8v2M10 14v2" />
      </Svg>
    </IconBase>
  ),
  microwave: ({ selected }) => (
    <IconBase selected={selected} title="Microwave">
      <Svg>
        <rect x="3" y="7" width="18" height="10" rx="2" />
        <path d="M7 11c1 0 2 1 3 1s2-1 3-1" />
        <circle cx="18" cy="12" r="1" />
      </Svg>
    </IconBase>
  ),
  dishwasher: ({ selected }) => (
    <IconBase selected={selected} title="Dishwasher">
      <Svg>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <circle cx="10" cy="13" r="2" />
        <circle cx="14" cy="13" r="2" />
        <path d="M8 8h8" />
      </Svg>
    </IconBase>
  ),
  coffee_maker: ({ selected }) => (
    <IconBase selected={selected} title="Coffee maker">
      <Svg>
        <path d="M6 6h12v3a5 5 0 0 1-5 5H11a5 5 0 0 1-5-5V6z" />
        <path d="M16 9h2a2 2 0 0 0 0-4h-2" />
        <path d="M9 17h6v2H9z" />
      </Svg>
    </IconBase>
  ),
  cooking_basics: ({ selected }) => (
    <IconBase selected={selected} title="Cooking basics">
      <Svg>
        <rect x="5" y="9" width="14" height="8" rx="2" />
        <path d="M9 9V7h6v2" />
      </Svg>
    </IconBase>
  ),
  oven: ({ selected }) => (
    <IconBase selected={selected} title="Oven">
      <Svg>
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="M7 8h10" />
        <circle cx="8" cy="6.5" r="0.8" />
        <circle cx="11" cy="6.5" r="0.8" />
        <circle cx="14" cy="6.5" r="0.8" />
      </Svg>
    </IconBase>
  ),
  stove: ({ selected }) => (
    <IconBase selected={selected} title="Stove">
      <Svg>
        <rect x="4" y="6" width="16" height="12" rx="2" />
        <circle cx="9" cy="10" r="2" />
        <circle cx="15" cy="10" r="2" />
      </Svg>
    </IconBase>
  ),
  free_parking: ({ selected }) => (
    <IconBase selected={selected} title="Free parking">
      <Svg>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <path d="M9 16V8h4a3 3 0 0 1 0 6H9z" />
      </Svg>
    </IconBase>
  ),
  ev_charger: ({ selected }) => (
    <IconBase selected={selected} title="EV charger">
      <Svg>
        <path d="M7 7h6v10H7z" />
        <path d="M13 10l4-2-2 4 4-2" />
      </Svg>
    </IconBase>
  ),
  pool: ({ selected }) => (
    <IconBase selected={selected} title="Pool">
      <Svg>
        <path d="M4 15c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2" />
        <path d="M4 18c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2" />
      </Svg>
    </IconBase>
  ),
  hot_tub: ({ selected }) => (
    <IconBase selected={selected} title="Hot tub">
      <Svg>
        <rect x="4" y="10" width="16" height="7" rx="2" />
        <path d="M8 8c2-2 0-3 2-5M12 8c2-2 0-3 2-5" />
      </Svg>
    </IconBase>
  ),
  gym: ({ selected }) => (
    <IconBase selected={selected} title="Gym">
      <Svg>
        <rect x="5" y="10" width="3" height="4" />
        <rect x="16" y="10" width="3" height="4" />
        <rect x="9" y="9" width="6" height="6" rx="1" />
      </Svg>
    </IconBase>
  ),
  bbq_grill: ({ selected }) => (
    <IconBase selected={selected} title="BBQ grill">
      <Svg>
        <path d="M6 9h12M7 9a5 5 0 0 0 10 0" />
        <path d="M9 14l-2 5M15 14l2 5M8 18h8" />
      </Svg>
    </IconBase>
  ),
  patio: ({ selected }) => (
    <IconBase selected={selected} title="Patio / deck">
      <Svg>
        <path d="M4 18h16M6 15h12M8 12h8" />
        <path d="M12 6l3 3h-6l3-3z" />
      </Svg>
    </IconBase>
  ),
  indoor_fireplace: ({ selected }) => (
    <IconBase selected={selected} title="Indoor fireplace">
      <Svg>
        <rect x="5" y="5" width="14" height="14" rx="2" />
        <path d="M12 10c1.5 1.5.5 3-1 4 2 0 4-1 4-3 0-1.5-1-3-3-5" />
      </Svg>
    </IconBase>
  ),
  crib: ({ selected }) => (
    <IconBase selected={selected} title="Crib">
      <Svg>
        <rect x="5" y="10" width="14" height="6" rx="1.5" />
        <path d="M7 10v-3M10 10V7M13 10V7M16 10V7" />
      </Svg>
    </IconBase>
  ),
  high_chair: ({ selected }) => (
    <IconBase selected={selected} title="High chair">
      <Svg>
        <rect x="8" y="7" width="8" height="4" rx="1" />
        <path d="M9 11l-2 7M15 11l2 7M8 15h8" />
      </Svg>
    </IconBase>
  ),
  bath: ({ selected }) => (
    <IconBase selected={selected} title="Bathtub">
      <Svg>
        <rect x="4" y="12" width="16" height="5" rx="2" />
        <path d="M8 12V9a2 2 0 0 1 2-2h2" />
        <path d="M12 7v2" />
      </Svg>
    </IconBase>
  ),
  board_games: ({ selected }) => (
    <IconBase selected={selected} title="Board games">
      <Svg>
        <rect x="6" y="6" width="12" height="12" rx="2" />
        <circle cx="10" cy="10" r="0.8" />
        <circle cx="14" cy="10" r="0.8" />
        <circle cx="10" cy="14" r="0.8" />
        <circle cx="14" cy="14" r="0.8" />
      </Svg>
    </IconBase>
  ),
  smoke_alarm: ({ selected }) => (
    <IconBase selected={selected} title="Smoke alarm">
      <Svg>
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="1" />
        <path d="M6 12h2M16 12h2M12 6v2M12 16v2" />
      </Svg>
    </IconBase>
  ),
  co_alarm: ({ selected }) => (
    <IconBase selected={selected} title="CO alarm">
      <Svg>
        <rect x="6" y="7" width="12" height="10" rx="2" />
        <path d="M9 12h6" />
        <circle cx="9" cy="10" r="0.6" />
        <circle cx="15" cy="10" r="0.6" />
      </Svg>
    </IconBase>
  ),
  first_aid: ({ selected }) => (
    <IconBase selected={selected} title="First aid kit">
      <Svg>
        <rect x="5" y="9" width="14" height="9" rx="2" />
        <rect x="9" y="5" width="6" height="4" rx="1" />
        <path d="M12 11v4M10 13h4" />
      </Svg>
    </IconBase>
  ),
  fire_extinguisher: ({ selected }) => (
    <IconBase selected={selected} title="Fire extinguisher">
      <Svg>
        <path d="M10 7h6l2-2-2-2-6 2z" />
        <rect x="8" y="9" width="8" height="10" rx="2" />
        <path d="M8 12h8" />
      </Svg>
    </IconBase>
  ),
  security_cameras: ({ selected }) => (
    <IconBase selected={selected} title="Security cameras">
      <Svg>
        <path d="M4 10l10 3 5-5-10-3z" />
        <path d="M14 13l2 6M6 11l-1 3" />
      </Svg>
    </IconBase>
  ),
  step_free_access: ({ selected }) => (
    <IconBase selected={selected} title="Step-free access">
      <Svg>
        <path d="M4 16h12l4-4" />
        <path d="M4 20h16" />
      </Svg>
    </IconBase>
  ),
  wide_doorway: ({ selected }) => (
    <IconBase selected={selected} title="Wide doorway">
      <Svg>
        <rect x="6" y="4" width="12" height="16" rx="2" />
        <path d="M10 4v16M14 7v10" />
      </Svg>
    </IconBase>
  ),
  accessible_parking: ({ selected }) => (
    <IconBase selected={selected} title="Accessible parking">
      <Svg>
        <circle cx="9" cy="7" r="1.5" />
        <path d="M8 9l1.5 3H15" />
        <path d="M10 12l-2 5M14 12l2 5" />
      </Svg>
    </IconBase>
  ),
  _default: ({ selected }) => (
    <IconBase selected={selected} title="">
      <Svg>
        <path d="M12 6v12M6 12h12" />
      </Svg>
    </IconBase>
  ),
};

/** --------------------------- Amenities -------------------------------- **/
const AMENITY_GROUPS = [
  {
    group: "Essentials",
    items: [
      { key: "wifi", label: "Wi-Fi" },
      { key: "tv", label: "TV" },
      { key: "heating", label: "Heating" },
      { key: "ac", label: "Air conditioning" },
      { key: "washer", label: "Washer" },
      { key: "dryer", label: "Dryer" },
      { key: "workspace", label: "Dedicated workspace" },
      { key: "iron", label: "Iron" },
      { key: "hair_dryer", label: "Hair dryer" },
    ],
  },
  {
    group: "Kitchen",
    items: [
      { key: "kitchen", label: "Full kitchen" },
      { key: "refrigerator", label: "Refrigerator" },
      { key: "microwave", label: "Microwave" },
      { key: "dishwasher", label: "Dishwasher" },
      { key: "coffee_maker", label: "Coffee maker" },
      { key: "cooking_basics", label: "Cooking basics" },
      { key: "oven", label: "Oven" },
      { key: "stove", label: "Stove" },
    ],
  },
  {
    group: "Parking & Facilities",
    items: [
      { key: "free_parking", label: "Free parking" },
      { key: "ev_charger", label: "EV charger" },
      { key: "pool", label: "Pool" },
      { key: "hot_tub", label: "Hot tub" },
      { key: "gym", label: "Gym" },
      { key: "bbq_grill", label: "BBQ grill" },
      { key: "patio", label: "Patio / deck" },
      { key: "indoor_fireplace", label: "Indoor fireplace" },
    ],
  },
  {
    group: "Family",
    items: [
      { key: "crib", label: "Crib" },
      { key: "high_chair", label: "High chair" },
      { key: "bath", label: "Bathtub" },
      { key: "board_games", label: "Board games" },
    ],
  },
  {
    group: "Safety",
    items: [
      { key: "smoke_alarm", label: "Smoke alarm" },
      { key: "co_alarm", label: "CO alarm" },
      { key: "first_aid", label: "First aid kit" },
      { key: "fire_extinguisher", label: "Fire extinguisher" },
      { key: "security_cameras", label: "Security cameras (disclosed)" },
    ],
  },
  {
    group: "Accessibility (self-reported)",
    items: [
      { key: "step_free_access", label: "Step-free access" },
      { key: "wide_doorway", label: "Wide doorway" },
      { key: "accessible_parking", label: "Accessible parking" },
    ],
  },
];

/** --------------- Amenity normalization (underscore style) ------------- **/
const normalizeAmenityKey = (x) =>
  String(x || "")
    .trim()
    .toLowerCase()
    .replace(/[.\s/-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_{2,}/g, "_");

/** --------------- Canvas->Blob (Safari fallback safe) ------------------ **/
function canvasToBlob(canvas, type = "image/jpeg", quality = IMG_QUALITY) {
  return new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((b) => resolve(b), type, quality);
    } else {
      const dataUrl = canvas.toDataURL(type, quality);
      const byteString = atob(dataUrl.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      resolve(new Blob([ab], { type }));
    }
  });
}

/** ---------------- Client-side image compression ----------------------- **/
async function compressImage(file, maxDim = IMG_MAX_DIM, quality = IMG_QUALITY) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = (e) => rej(e);
      i.src = url;
    });

    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await canvasToBlob(canvas, "image/jpeg", quality);
    if (!blob) throw new Error("Compression failed");
    return new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** ----------------------------- Debounce ------------------------------- **/
function useDebouncedEffect(fn, deps, delay) {
  useEffect(() => {
    const id = setTimeout(fn, delay);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, delay]);
}

/** ------------------------ Unsaved changes guard ----------------------- **/
function useUnsavedChanges(isDirty) {
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);
}

/** ============================== Page ================================= **/
export default function CreateListing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  /** ------------------ Step + validation refs ------------------------- **/
  const [step, setStep] = useState(1);
  const titleRef = useRef(null);
  const summaryRef = useRef(null);
  const maxGuestsDayRef = useRef(null);
  const maxGuestsOvernightRef = useRef(null);
  const addressRef = useRef(null);
  const cityRef = useRef(null);
  const stateRef = useRef(null);
  const postalCodeRef = useRef(null);
  const priceRef = useRef(null);
  const nightlyRef = useRef(null);
  const minNightsRef = useRef(null);
  const maxNightsRef = useRef(null);
  const checkInRef = useRef(null);
  const checkOutRef = useRef(null);
  const dayStartRef = useRef(null);
  const dayEndRef = useRef(null);
  const spaceNameRef = useRef(null);
  const approvalHoursRef = useRef(null);
  const bedroomsRef = useRef(null);
  const bedsRef = useRef(null);
  const bathroomsRef = useRef(null);
  const parkingSpotsRef = useRef(null);
  const parkingNotesRef = useRef(null);
  const quietHoursStartRef = useRef(null);
  const quietHoursEndRef = useRef(null);

  /** ------------------------- Form state ------------------------------ **/
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [maxGuestsDay, setMaxGuestsDay] = useState("");
  const [maxGuestsOvernight, setMaxGuestsOvernight] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [countryCode, setCountryCode] = useState("US");

  // NEW: Absolute must-haves
  const [propertyType, setPropertyType] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [beds, setBeds] = useState("");
  const [bathrooms, setBathrooms] = useState("");

  const [spaceType, setSpaceType] = useState("entire_place");
  const [spaceName, setSpaceName] = useState("");
  const [entranceType, setEntranceType] = useState("shared");
  const [areas, setAreas] = useState([]);

  // amenities: store normalized underscore keys
  const [amenities, setAmenities] = useState([]);
  const [amenityQuery, setAmenityQuery] = useState("");

  const addAmenities = useCallback((keys) => {
    const normalized = keys.map(normalizeAmenityKey).filter(Boolean);
    setAmenities((prev) => Array.from(new Set([...prev, ...normalized])));
  }, []);
  const clearAmenities = useCallback(() => setAmenities([]), []);
  const toggleAmenity = useCallback((key) => {
    const k = normalizeAmenityKey(key);
    setAmenities((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  }, []);

  // NEW: Strongly-recommended fields
  const [parkingSpotsOnsite, setParkingSpotsOnsite] = useState("");
  const [parkingNotes, setParkingNotes] = useState("");
  const [suitableForLoudMusic, setSuitableForLoudMusic] = useState(null);
  const [quietHoursStart, setQuietHoursStart] = useState("");
  const [quietHoursEnd, setQuietHoursEnd] = useState("");
  const [sharedWalls, setSharedWalls] = useState(null);
  const [goodForEventsTypes, setGoodForEventsTypes] = useState([]);
  const [suitableForChildren, setSuitableForChildren] = useState("");
  const [suitableForInfants, setSuitableForInfants] = useState(null);
  const [petsAllowed, setPetsAllowed] = useState(null);
  const [hostLivesOnsite, setHostLivesOnsite] = useState("");
  const [isPrimaryResidence, setIsPrimaryResidence] = useState(null);

  // Booking mode & pricing/rules
  const [bookingMode, setBookingMode] = useState("day");
  const [dayPrice, setDayPrice] = useState("");
  const [dayUseStartHour, setDayUseStartHour] = useState("");
  const [dayUseEndHour, setDayUseEndHour] = useState("");

  const [nightlyPrice, setNightlyPrice] = useState("");
  const [minNights, setMinNights] = useState("");
  const [maxNights, setMaxNights] = useState("");
  const [checkInHour, setCheckInHour] = useState("");
  const [checkOutHour, setCheckOutHour] = useState("");

  // Things to know
  const [rules, setRules] = useState("");
  const [safetyNotes, setSafetyNotes] = useState("");
  const [otherNotes, setOtherNotes] = useState("");

  // NEW: Approval model - FIXED: Proper boolean handling
  const [instantBook, setInstantBook] = useState(true);
  // Approval window is fixed at 4 hours - hosts cannot modify
  const approvalWindowHours = 4;

  /** ---------------------- Location autocomplete + geo ---------------- **/
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressAutocompleteError, setAddressAutocompleteError] = useState("");
  const [geocodeResult, setGeocodeResult] = useState(null);
  const [geocodingStatus, setGeocodingStatus] = useState("idle");

  /** -------------------------- Listing Photos ------------------------- **/
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  /** ---------------------- Verification state ------------------------- **/
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [idProgress, setIdProgress] = useState(0);
  const [idStatus, setIdStatus] = useState("unverified");
  const [idNote, setIdNote] = useState("");

  const [ownershipType, setOwnershipType] = useState("owner");
  const [rtlDoc, setRtlDoc] = useState(null);
  const [landlordConsent, setLandlordConsent] = useState(null);
  const [rtlProgress, setRtlProgress] = useState(0);
  const [rtlStatus, setRtlStatus] = useState("unverified");
  const [rtlNote, setRtlNote] = useState("");

  /** ---------------------- Validation + status ------------------------ **/
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [globalMsg, setGlobalMsg] = useState("");
  const [fileWarning, setFileWarning] = useState("");
  const [suggestedPrice, setSuggestedPrice] = useState(null);
  const [suggestedNightly, setSuggestedNightly] = useState(null);
  const [submittingVerification, setSubmittingVerification] = useState(false);

  /** ------------------------ Dirty tracking --------------------------- **/
  const dirty =
    !!title ||
    !!summary ||
    !!maxGuestsDay ||
    !!maxGuestsOvernight ||
    !!address ||
    !!city ||
    !!stateCode ||
    !!postalCode ||
    countryCode !== "US" ||
    !!dayPrice ||
    !!nightlyPrice ||
    !!minNights ||
    !!maxNights ||
    !!checkInHour ||
    !!checkOutHour ||
    !!dayUseStartHour ||
    !!dayUseEndHour ||
    !!rules ||
    !!safetyNotes ||
    !!otherNotes ||
    files.length > 0 ||
    idFront ||
    idBack ||
    selfie ||
    rtlDoc ||
    landlordConsent ||
    spaceType !== "entire_place" ||
    !!spaceName ||
    entranceType !== "shared" ||
    areas.length > 0 ||
    amenities.length > 0 ||
    instantBook === false ||
    // NEW: Absolute must-haves
    !!propertyType ||
    !!bedrooms ||
    !!beds ||
    !!bathrooms ||
    // NEW: Strongly-recommended
    !!parkingSpotsOnsite ||
    !!parkingNotes ||
    suitableForLoudMusic !== null ||
    !!quietHoursStart ||
    !!quietHoursEnd ||
    sharedWalls !== null ||
    goodForEventsTypes.length > 0 ||
    !!suitableForChildren ||
    suitableForInfants !== null ||
    petsAllowed !== null ||
    !!hostLivesOnsite ||
    isPrimaryResidence !== null;

  useUnsavedChanges(dirty);

  /** ------------------------- Load draft ------------------------------ **/
  useEffect(() => {
    const draftRaw = localStorage.getItem(DRAFT_KEY);
    if (!draftRaw) return;
    try {
      const d = JSON.parse(draftRaw);
      setTitle(d.title || "");
      setSummary(d.summary || "");
      setMaxGuestsDay(d.maxGuestsDay || "");
      setMaxGuestsOvernight(d.maxGuestsOvernight || "");
      setAddress(d.address || "");
      setCity(d.city || "");
      setStateCode(d.stateCode || "");
      setPostalCode(d.postalCode || "");
      setCountryCode(d.countryCode || "US");
      // NEW: Absolute must-haves
      setPropertyType(d.propertyType || "");
      setBedrooms(d.bedrooms || "");
      setBeds(d.beds || "");
      setBathrooms(d.bathrooms || "");
      setBookingMode(d.bookingMode || "day");
      setDayPrice(d.dayPrice ?? "");
      setDayUseStartHour(d.dayUseStartHour ?? "");
      setDayUseEndHour(d.dayUseEndHour ?? "");
      setNightlyPrice(d.nightlyPrice ?? "");
      setMinNights(d.minNights ?? "");
      setMaxNights(d.maxNights ?? "");
      setCheckInHour(d.checkInHour ?? "");
      setCheckOutHour(d.checkOutHour ?? "");
      setRules(d.rules || "");
      setSafetyNotes(d.safetyNotes || "");
      setOtherNotes(d.otherNotes || "");
      setSpaceType(d.spaceType || "entire_place");
      setSpaceName(d.spaceName || "");
      setEntranceType(d.entranceType || "shared");
      setAreas(Array.isArray(d.areas) ? d.areas : []);
      setAmenities(
        Array.isArray(d.amenities)
          ? Array.from(new Set(d.amenities.map(normalizeAmenityKey)))
          : []
      );
      // NEW: Strongly-recommended
      setParkingSpotsOnsite(d.parkingSpotsOnsite ?? "");
      setParkingNotes(d.parkingNotes || "");
      setSuitableForLoudMusic(d.suitableForLoudMusic ?? null);
      setQuietHoursStart(d.quietHoursStart ?? "");
      setQuietHoursEnd(d.quietHoursEnd ?? "");
      setSharedWalls(d.sharedWalls ?? null);
      setGoodForEventsTypes(Array.isArray(d.goodForEventsTypes) ? d.goodForEventsTypes : []);
      setSuitableForChildren(d.suitableForChildren || "");
      setSuitableForInfants(d.suitableForInfants ?? null);
      setPetsAllowed(d.petsAllowed ?? null);
      setHostLivesOnsite(d.hostLivesOnsite || "");
      setIsPrimaryResidence(d.isPrimaryResidence ?? null);
      
      // FIXED: Proper boolean parsing for instantBook
      if (d.instantBook !== undefined) {
        if (typeof d.instantBook === 'string') {
          setInstantBook(d.instantBook === 'true');
        } else {
          setInstantBook(!!d.instantBook);
        }
      } else {
        setInstantBook(true);
      }
    } catch {
      // ignore malformed draft
    }
  }, []);

  /** ------------------ Fetch verification status (host) --------------- **/
  const refreshVerificationStatus = useCallback(async () => {
    if (!user) return;
    try {
      const s = await getHostVerificationStatus({ uid: user.uid });
      if (s?.idVerification) {
        setIdStatus(s.idVerification.status || "unverified");
        setIdNote(s.idVerification.note || "");
      }
      if (s?.rightToList) {
        setRtlStatus(s.rightToList.status || "unverified");
        setRtlNote(s.rightToList.note || "");
        if (s.rightToList.type) setOwnershipType(s.rightToList.type);
      }
    } catch {
      // no-op
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    refreshVerificationStatus();
  }, [user, refreshVerificationStatus]);

  /** ------------------------- Auto-save ------------------------------- **/
  useDebouncedEffect(
    () => {
      const draft = {
        title,
        summary,
        maxGuestsDay,
        maxGuestsOvernight,
        address,
        city,
        stateCode,
        postalCode,
        countryCode,
        // NEW: Absolute must-haves
        propertyType,
        bedrooms,
        beds,
        bathrooms,
        bookingMode,
        dayPrice,
        dayUseStartHour,
        dayUseEndHour,
        nightlyPrice,
        minNights,
        maxNights,
        checkInHour,
        checkOutHour,
        rules,
        safetyNotes,
        otherNotes,
        spaceType,
        spaceName,
        entranceType,
        areas,
        amenities: Array.from(new Set(amenities.map(normalizeAmenityKey))),
        // NEW: Strongly-recommended
        parkingSpotsOnsite,
        parkingNotes,
        suitableForLoudMusic,
        quietHoursStart,
        quietHoursEnd,
        sharedWalls,
        goodForEventsTypes,
        suitableForChildren,
        suitableForInfants,
        petsAllowed,
        hostLivesOnsite,
        isPrimaryResidence,
        // FIXED: Store as proper boolean
        instantBook,
        savedAt: Date.now(),
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      if (dirty) {
        setGlobalMsg("Draft saved");
        const t = setTimeout(() => setGlobalMsg(""), 1200);
        return () => clearTimeout(t);
      }
    },
    [
      title,
      summary,
      maxGuestsDay,
      maxGuestsOvernight,
      address,
      city,
      stateCode,
      postalCode,
      countryCode,
      propertyType,
      bedrooms,
      beds,
      bathrooms,
      bookingMode,
      dayPrice,
      dayUseStartHour,
      dayUseEndHour,
      nightlyPrice,
      minNights,
      maxNights,
      checkInHour,
      checkOutHour,
      rules,
      safetyNotes,
      otherNotes,
      spaceType,
      spaceName,
      entranceType,
      areas,
      amenities,
      parkingSpotsOnsite,
      parkingNotes,
      suitableForLoudMusic,
      quietHoursStart,
      quietHoursEnd,
      sharedWalls,
      goodForEventsTypes,
      suitableForChildren,
      suitableForInfants,
      petsAllowed,
      hostLivesOnsite,
      isPrimaryResidence,
      instantBook,
      dirty,
    ],
    500
  );

  /** --------- Dynamic price suggestion (local heuristic) -------------- **/
  useDebouncedEffect(
    () => {
      const c = Number(maxGuestsDay || maxGuestsOvernight || 0);
      if (!city || !stateCode || !c) {
        setSuggestedPrice(null);
        setSuggestedNightly(null);
        return;
      }
      const s = suggestDayRate({ city, stateCode, capacity: c });
      setSuggestedPrice(s);
      setSuggestedNightly(Math.max(50, Math.round(s * 0.8)));
    },
    [city, stateCode, maxGuestsDay, maxGuestsOvernight],
    300
  );

  /** --------- Address autocomplete (Places callable) ------------------ **/
  useDebouncedEffect(
    () => {
      setAddressAutocompleteError("");
      setAddressSuggestions([]);

      const base = address?.trim() || "";
      if (base.length < 3) {
        setAddressLoading(false);
        return;
      }

      const input = [address, city, stateCode].filter(Boolean).join(", ").trim();
      if (input.length < 3) {
        setAddressLoading(false);
        return;
      }

      let cancelled = false;
      (async () => {
        try {
          setAddressLoading(true);
          const res = await placesAutocompleteFn({
            input,
            countryCode: countryCode || "US",
          });
          if (cancelled) return;
          const data = res.data || {};
          if (!data.ok || !Array.isArray(data.predictions)) {
            setAddressAutocompleteError(
              "Couldn't fetch suggestions. You can still type your address manually."
            );
            setAddressSuggestions([]);
            return;
          }
          setAddressSuggestions(data.predictions);
        } catch (err) {
          if (cancelled) return;
          console.error(err);
          setAddressAutocompleteError(
            "Couldn't fetch suggestions. You can still type your address manually."
          );
          setAddressSuggestions([]);
        } finally {
          if (!cancelled) setAddressLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    },
    [address, city, stateCode, countryCode],
    450
  );

  /** --------------------- Build image previews ------------------------ **/
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  /** ------------------------- Derived pricing ------------------------- **/
  const guestDayDisplay = useMemo(
    () => computeGuestDisplay(Number(dayPrice || 0), ROUND_STEP_DAY),
    [dayPrice]
  );
  const guestNightDisplay = useMemo(
    () => computeGuestDisplay(Number(nightlyPrice || 0), ROUND_STEP_NIGHT),
    [nightlyPrice]
  );
  const hostDayPayout = useMemo(
    () => computeHostPayoutEstimate(Number(dayPrice || 0), HOST_FEE_RATE_EFFECTIVE),
    [dayPrice]
  );
  const hostNightPayout = useMemo(
    () => computeHostPayoutEstimate(Number(nightlyPrice || 0), HOST_FEE_RATE_EFFECTIVE),
    [nightlyPrice]
  );

  /** ------------------------- Validations ----------------------------- **/
  const listingReady = useMemo(() => {
    const baseReady =
      title.trim() &&
      summary.trim() &&
      Number(maxGuestsDay) > 0 &&
      Number(maxGuestsOvernight) > 0 &&
      address.trim() &&
      city.trim() &&
      stateCode.trim().length === 2 &&
      postalCode.trim() &&
      files.length > 0 &&
      spaceType &&
      (spaceType === "entire_place" || spaceName.trim().length >= 2) &&
      propertyType &&
      bedrooms &&
      beds &&
      bathrooms;

    if (!baseReady) return false;

    if (bookingMode === "day") {
      return Number(dayPrice) > 0;
    }
    if (bookingMode === "overnight") {
      const np = Number(nightlyPrice);
      const minN = Number(minNights || 0);
      const maxN = Number(maxNights || 0);
      const ci = checkInHour === "" ? 0 : Number(checkInHour);
      const co = checkOutHour === "" ? 0 : Number(checkOutHour);
      const hoursOk =
        (checkInHour === "" || (ci >= 0 && ci <= 23)) &&
        (checkOutHour === "" || (co >= 0 && co <= 23));
      return np > 0 && minN >= 1 && (!maxN || maxN >= minN) && hoursOk;
    }
    const dayOk = Number(dayPrice) > 0;
    const nightOk =
      Number(nightlyPrice) > 0 &&
      Number(minNights || 0) >= 1 &&
      (!Number(maxNights || 0) || Number(maxNights || 0) >= Number(minNights || 0));
    return dayOk && nightOk;
  }, [
    bookingMode,
    title,
    summary,
    maxGuestsDay,
    maxGuestsOvernight,
    address,
    city,
    stateCode,
    postalCode,
    files.length,
    dayPrice,
    nightlyPrice,
    minNights,
    maxNights,
    checkInHour,
    checkOutHour,
    spaceType,
    spaceName,
    propertyType,
    bedrooms,
    beds,
    bathrooms,
  ]);

  const canPublish = listingReady;

  const focusFirstError = useCallback((e) => {
    if (e.title) titleRef.current?.focus();
    else if (e.summary) summaryRef.current?.focus();
    else if (e.maxGuestsDay) maxGuestsDayRef.current?.focus();
    else if (e.maxGuestsOvernight) maxGuestsOvernightRef.current?.focus();
    else if (e.address) addressRef.current?.focus();
    else if (e.city) cityRef.current?.focus();
    else if (e.stateCode) stateRef.current?.focus();
    else if (e.postalCode) postalCodeRef.current?.focus();
    else if (e.spaceName) spaceNameRef.current?.focus();
    else if (e.propertyType) document.querySelector('select[name="propertyType"]')?.focus();
    else if (e.bedrooms) bedroomsRef.current?.focus();
    else if (e.beds) bedsRef.current?.focus();
    else if (e.bathrooms) bathroomsRef.current?.focus();
    else if (e.dayPrice) priceRef.current?.focus();
    else if (e.nightlyPrice) nightlyRef.current?.focus();
    else if (e.minNights) minNightsRef.current?.focus();
    else if (e.maxNights) maxNightsRef.current?.focus();
    else if (e.checkInHour) checkInRef.current?.focus();
    else if (e.checkOutHour) checkOutRef.current?.focus();
    else if (e.dayUseStartHour) dayStartRef.current?.focus();
    else if (e.dayUseEndHour) dayEndRef.current?.focus();
    else if (e.approvalWindowHours) approvalHoursRef.current?.focus();
    else if (e.quietHoursStart) quietHoursStartRef.current?.focus();
    else if (e.quietHoursEnd) quietHoursEndRef.current?.focus();
  }, []);

  const validate = useCallback(
    (currentStep = step) => {
      const e = {};

      // Step 3+ — title & summary
      if (currentStep >= 3) {
        if (!title.trim()) e.title = "Title is required.";
        if (!summary.trim()) e.summary = "Summary is required.";
      }

      // Step 4+ — space details
      if (currentStep >= 4) {
        if (!spaceType) e.spaceType = "Choose a space type.";
        if (spaceType !== "entire_place" && !spaceName.trim())
          e.spaceName = "Name this room/space so guests can tell them apart.";
        if (!propertyType) e.propertyType = "Select property type.";
      }

      // Step 5+ — capacity & beds
      if (currentStep >= 5) {
        if (!maxGuestsDay || Number(maxGuestsDay) <= 0)
          e.maxGuestsDay = "Event capacity is required.";
        if (!maxGuestsOvernight || Number(maxGuestsOvernight) <= 0)
          e.maxGuestsOvernight = "Sleeping capacity is required.";
        if (Number(maxGuestsOvernight) > Number(maxGuestsDay) && maxGuestsDay && maxGuestsOvernight)
          e.maxGuestsOvernight = "Sleeping capacity cannot exceed event capacity.";
        if (!bedrooms || Number(bedrooms) < 0) e.bedrooms = "Number of bedrooms is required.";
        if (!beds || Number(beds) <= 0) e.beds = "Total number of beds is required.";
        if (!bathrooms || Number(bathrooms) <= 0) e.bathrooms = "Number of bathrooms is required.";
      }

      // Step 6+ — location
      if (currentStep >= 6) {
        if (!address.trim()) e.address = "Address is required.";
        if (!city.trim()) e.city = "City is required.";
        if (!stateCode.trim() || stateCode.trim().length !== 2)
          e.stateCode = "State (2-letter) is required.";
        if (!postalCode.trim()) e.postalCode = "Postal code is required.";
      }

      // Step 8+ — neighbors & noise
      if (currentStep >= 8) {
        if (quietHoursStart && quietHoursEnd) {
          const start = parseInt(quietHoursStart);
          const end = parseInt(quietHoursEnd);
          if (start >= end) e.quietHoursEnd = "End time must be after start time.";
        }
      }

      // Step 9+ — photos
      if (currentStep >= 9) {
        if (!files.length) e.photos = "Add at least one photo.";
      }

      // Step 10+ — usage & pricing
      if (currentStep >= 10) {
        if (bookingMode === "day" || bookingMode === "hybrid") {
          if (!dayPrice || Number(dayPrice) <= 0)
            e.dayPrice = "Enter a valid day rate.";
          const ds = dayUseStartHour === "" ? null : Number(dayUseStartHour);
          const de = dayUseEndHour === "" ? null : Number(dayUseEndHour);
          if (ds != null && de != null && !(ds < de))
            e.dayUseEndHour = "End time must be after start time.";
        }

        if (bookingMode === "overnight" || bookingMode === "hybrid") {
          if (!nightlyPrice || Number(nightlyPrice) <= 0)
            e.nightlyPrice = "Enter a valid nightly rate.";
          const minN = Number(minNights || 0);
          const maxN = Number(maxNights || 0);
          if (minN < 1) e.minNights = "Minimum nights must be at least 1.";
          if (maxN && maxN < minN) e.maxNights = "Max nights must be ≥ min nights.";
          const ci = checkInHour === "" ? null : Number(checkInHour);
          const co = checkOutHour === "" ? null : Number(checkOutHour);
          if (ci != null && co != null && !(ci >= 0 && ci <= 23 && co >= 0 && co <= 23)) {
            e.checkInHour = "Choose valid check-in and check-out times.";
          }
        }

        // FIXED: Remove validation for approvalWindowHours since it's fixed at 4
        // No validation needed - it's always 4
      }

      setErrors(e);
      if (Object.keys(e).length) focusFirstError(e);
      return Object.keys(e).length === 0;
    },
    [
      step,
      title,
      summary,
      maxGuestsDay,
      maxGuestsOvernight,
      address,
      city,
      stateCode,
      postalCode,
      files.length,
      bookingMode,
      dayPrice,
      dayUseStartHour,
      dayUseEndHour,
      nightlyPrice,
      minNights,
      maxNights,
      checkInHour,
      checkOutHour,
      spaceType,
      spaceName,
      propertyType,
      bedrooms,
      beds,
      bathrooms,
      quietHoursStart,
      quietHoursEnd,
      focusFirstError,
    ]
  );

  const next = () => {
    // Step 1 & 2 (verification) are optional – don't block on validation
    if (step === 1 || step === 2) {
      setStep((s) => Math.min(TOTAL_STEPS, s + 1));
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (validate(step)) {
      setStep((s) => Math.min(TOTAL_STEPS, s + 1));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const back = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /** ------------------- Handle address suggestion click ---------------- **/
  const handleAddressSuggestionClick = useCallback(
    async (prediction) => {
      if (!prediction?.placeId) return;
      setAddressAutocompleteError("");
      setAddressSuggestions([]);
      setGeocodingStatus("loading");
      try {
        const res = await geocodeListingAddressFn({
          placeId: prediction.placeId,
          countryCode: countryCode || "US",
        });
        const data = res.data || {};
        if (!data.ok || !data.result) {
          setGeocodingStatus("error");
          return;
        }
        const r = data.result;
        setGeocodeResult(r);
        if (r.addressStreet1) setAddress(r.addressStreet1);
        if (r.addressCity) setCity(r.addressCity);
        if (r.addressStateCode) setStateCode(r.addressStateCode);
        if (r.addressPostalCode) setPostalCode(r.addressPostalCode);
        if (r.addressCountryCode) setCountryCode(r.addressCountryCode);
        setGeocodingStatus("ok");
      } catch (err) {
        console.error(err);
        setGeocodingStatus("error");
      }
    },
    [countryCode]
  );

  /** -------------------------- File intake ---------------------------- **/
  const acceptSomeFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;

    const rejected = [];
    const filtered = incoming.filter((f) => {
      const okType = ACCEPTED_TYPES.includes(f.type);
      const okSize = f.size <= MAX_FILE_MB * 1024 * 1024;
      const ok = okType && okSize;
      if (!ok) rejected.push(f.name);
      return ok;
    });

    setFiles((prev) => {
      const key = (f) => `${f.name}:${f.size}:${f.lastModified}`;
      const seen = new Set(prev.map(key));
      const merged = [...prev];
      for (const f of filtered) {
        if (merged.length >= MAX_FILES) break;
        if (!seen.has(key(f))) {
          merged.push(f);
          seen.add(key(f));
        }
      }
      return merged;
    });

    setFileWarning(
      rejected.length
        ? `Skipped ${rejected.length} file(s): wrong type or > ${MAX_FILE_MB}MB`
        : ""
    );
  };

  const handleInputChange = (e) => acceptSomeFiles(e.target.files);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    acceptSomeFiles(e.dataTransfer.files);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };
  const handleDragLeave = () => setDragging(false);

  const removePhoto = (i) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  };
  const movePhoto = (i, dir) => {
    setFiles((prev) => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const clearPhotos = () => setFiles([]);

  /** -------------------------- Save/Exit helpers ---------------------- **/
  const saveDraftNow = useCallback(() => {
    const draft = {
      title,
      summary,
      maxGuestsDay,
      maxGuestsOvernight,
      address,
      city,
      stateCode,
      postalCode,
      countryCode,
      propertyType,
      bedrooms,
      beds,
      bathrooms,
      bookingMode,
      dayPrice,
      dayUseStartHour,
      dayUseEndHour,
      nightlyPrice,
      minNights,
      maxNights,
      checkInHour,
      checkOutHour,
      rules,
      safetyNotes,
      otherNotes,
      spaceType,
      spaceName,
      entranceType,
      areas,
      amenities: Array.from(new Set(amenities.map(normalizeAmenityKey))),
      parkingSpotsOnsite,
      parkingNotes,
      suitableForLoudMusic,
      quietHoursStart,
      quietHoursEnd,
      sharedWalls,
      goodForEventsTypes,
      suitableForChildren,
      suitableForInfants,
      petsAllowed,
      hostLivesOnsite,
      isPrimaryResidence,
      // FIXED: Store as proper boolean
      instantBook,
      savedAt: Date.now(),
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    setGlobalMsg("Draft saved");
    const t = setTimeout(() => setGlobalMsg(""), 1200);
    return () => clearTimeout(t);
  }, [
    title,
    summary,
    maxGuestsDay,
    maxGuestsOvernight,
    address,
    city,
    stateCode,
    postalCode,
    countryCode,
    propertyType,
    bedrooms,
    beds,
    bathrooms,
    bookingMode,
    dayPrice,
    dayUseStartHour,
    dayUseEndHour,
    nightlyPrice,
    minNights,
    maxNights,
    checkInHour,
    checkOutHour,
    rules,
    safetyNotes,
    otherNotes,
    spaceType,
    spaceName,
    entranceType,
    areas,
    amenities,
    parkingSpotsOnsite,
    parkingNotes,
    suitableForLoudMusic,
    quietHoursStart,
    quietHoursEnd,
    sharedWalls,
    goodForEventsTypes,
    suitableForChildren,
    suitableForInfants,
    petsAllowed,
    hostLivesOnsite,
    isPrimaryResidence,
    instantBook,
  ]);

  const exitToHome = useCallback(() => {
    const hasDirty = dirty;

    if (!hasDirty) {
      navigate("/", { replace: true });
      return;
    }
    const choice = window.confirm(
      "You have unsaved changes.\n\nClick OK to save draft and go home.\nClick Cancel to stay on this page."
    );
    if (choice) {
      saveDraftNow();
      navigate("/", { replace: true });
    }
  }, [dirty, navigate, saveDraftNow]);

  /** ----------------------------- Submit: Identity -------------------- **/
  const submitIdentity = async () => {
    if (!user) return;
    if (!idFront || !selfie) {
      setGlobalMsg("Please add ID front and a selfie (ID back is optional).");
      return;
    }
    setIdProgress(0);
    setGlobalMsg("");
    try {
      setIdStatus("pending");
      await submitIdAndSelfie({
        uid: user.uid,
        idFront,
        idBack,
        selfie,
        onProgress: (p) => setIdProgress(Math.round(p)),
      });
      await refreshVerificationStatus();
      setGlobalMsg("Identity submitted. We'll review shortly.");
    } catch (err) {
      console.error(err);
      setIdStatus("unverified");
      setGlobalMsg("We couldn't submit your identity. Please try again.");
    }
  };

  /** ----------------------- Submit: Right to List --------------------- **/
  const submitRightToListDocs = async () => {
    if (!user) return;
    if (ownershipType === "tenant" && !landlordConsent) {
      setGlobalMsg("Please upload a lease page or signed landlord/PM consent.");
      return;
    }
    if (!rtlDoc && ownershipType !== "tenant") {
      setGlobalMsg("Please upload a property/business document with your name and address.");
      return;
    }
    setRtlProgress(0);
    setGlobalMsg("");
    try {
      setRtlStatus("pending");
      await submitRightToList({
        uid: user.uid,
        ownershipType,
        docFile: rtlDoc || null,
        consentFile: landlordConsent || null,
        onProgress: (p) => setRtlProgress(Math.round(p)),
      });
      await refreshVerificationStatus();
      setGlobalMsg("Right-to-List submitted. We'll review shortly.");
    } catch (err) {
      console.error(err);
      setRtlStatus("unverified");
      setGlobalMsg("We couldn't submit your document. Please try again.");
    }
  };

  /** -------------- Helper: auto-submit verification on publish ------- **/
  const hasRtlFiles = useMemo(
    () =>
      (ownershipType === "tenant" && landlordConsent) ||
      (ownershipType !== "tenant" && rtlDoc),
    [ownershipType, landlordConsent, rtlDoc]
  );

  const ensureVerificationSubmittedBeforePublish = useCallback(async () => {
    if (!user) return;
    setSubmittingVerification(true);
    setIdProgress(0);
    setRtlProgress(0);
    try {
      if ((idStatus === "unverified" || idStatus === "rejected") && idFront && selfie) {
        setGlobalMsg("Submitting identity…");
        await submitIdAndSelfie({
          uid: user.uid,
          idFront,
          idBack,
          selfie,
          onProgress: (p) => setIdProgress(Math.round(p)),
        });
      }
      if ((rtlStatus === "unverified" || rtlStatus === "rejected") && hasRtlFiles) {
        setGlobalMsg("Submitting right-to-list…");
        await submitRightToList({
          uid: user.uid,
          ownershipType,
          docFile: rtlDoc || null,
          consentFile: landlordConsent || null,
          onProgress: (p) => setRtlProgress(Math.round(p)),
        });
      }
      await refreshVerificationStatus();
      setGlobalMsg(
        "Verification submitted (if provided). Your listing will show as pending until approval."
      );
    } catch (e) {
      console.error(e);
      setGlobalMsg(
        "We couldn't submit your verification automatically. You can still publish now and retry from Step 1."
      );
    } finally {
      setSubmittingVerification(false);
    }
  }, [
    user,
    idStatus,
    rtlStatus,
    idFront,
    idBack,
    selfie,
    hasRtlFiles,
    ownershipType,
    rtlDoc,
    landlordConsent,
    refreshVerificationStatus,
  ]);

  /** ----------------------------- Publish ----------------------------- **/
  const publish = async () => {
    if (!user) return;
    if (!validate(TOTAL_STEPS)) return;

    await ensureVerificationSubmittedBeforePublish();

    setSaving(true);
    setUploadProgress(0);
    setGlobalMsg("");

    try {
      // Best-effort geocode before publish (if we don't already have it)
      let finalGeo = geocodeResult;
      if (!finalGeo) {
        try {
          setGeocodingStatus("loading");
          const res = await geocodeListingAddressFn({
            address: address.trim(),
            city: city.trim(),
            stateCode: stateCode.trim(),
            postalCode: postalCode.trim(),
            countryCode: countryCode || "US",
          });
          const data = res.data || {};
          if (data.ok && data.result) {
            finalGeo = data.result;
            setGeocodeResult(data.result);
            setGeocodingStatus("ok");
          } else {
            setGeocodingStatus("error");
          }
        } catch (geoErr) {
          console.error(geoErr);
          setGeocodingStatus("error");
        }
      }

      const compressed = [];
      for (const f of files) {
        try {
          const c = await compressImage(f, IMG_MAX_DIM, IMG_QUALITY);
          compressed.push(c);
        } catch {
          compressed.push(f);
        }
      }

      // normalize + dedupe amenity keys
      const amenityKeys = Array.from(new Set(amenities.map(normalizeAmenityKey)));
      const features = Object.fromEntries(amenityKeys.map((k) => [k, true]));

      // Derived prices (optional payload fields; backend can ignore safely)
      const guestDisplayDayPrice =
        bookingMode === "overnight" ? null : guestDayDisplay ?? null;
      const guestDisplayNightlyPrice =
        bookingMode === "day" ? null : guestNightDisplay ?? null;

      const payload = {
        ownerUid: user.uid,
        ownerEmail: user.email || "",
        title: title.trim(),
        summary: summary.trim(),
        maxGuestsDay: Number(maxGuestsDay),
        maxGuestsOvernight: Number(maxGuestsOvernight),
        address: address.trim(),
        city: city.trim(),
        stateCode: stateCode.trim(),
        postalCode: postalCode.trim() || null,
        countryCode: countryCode || "US",

        // NEW: Absolute must-haves
        propertyType,
        bedrooms: Number(bedrooms) || 0,
        beds: Number(beds) || 0,
        bathrooms: parseFloat(bathrooms) || 0,

        // space typing
        spaceType,
        spaceName: spaceName.trim() || null,
        entranceType,
        areas,

        // amenities (array) + features (boolean map)
        amenities: amenityKeys,
        amenitiesSelected: amenityKeys,
        features,

        // NEW: Strongly-recommended
        parkingSpotsOnsite: parkingSpotsOnsite ? Number(parkingSpotsOnsite) : null,
        parkingNotes: parkingNotes.trim() || null,
        suitableForLoudMusic,
        quietHoursStart: quietHoursStart || null,
        quietHoursEnd: quietHoursEnd || null,
        sharedWalls,
        goodForEventsTypes,
        suitableForChildren,
        suitableForInfants,
        petsAllowed,
        hostLivesOnsite,
        isPrimaryResidence,

        // booking: BASE prices (source of truth)
        bookingMode,
        dayPrice:
          bookingMode === "overnight" ? null : Number(dayPrice || 0) || null,
        dayUseStartHour:
          bookingMode === "overnight"
            ? null
            : dayUseStartHour === ""
            ? null
            : Number(dayUseStartHour),
        dayUseEndHour:
          bookingMode === "overnight"
            ? null
            : dayUseEndHour === ""
            ? null
            : Number(dayUseEndHour),
        nightlyPrice:
          bookingMode === "day" ? null : Number(nightlyPrice || 0) || null,
        minNights:
          bookingMode === "day"
            ? null
            : minNights === ""
            ? null
            : Number(minNights),
        maxNights:
          bookingMode === "day"
            ? null
            : maxNights === ""
            ? null
            : Number(maxNights),
        checkInHour:
          bookingMode === "day"
            ? null
            : checkInHour === ""
            ? null
            : Number(checkInHour),
        checkOutHour:
          bookingMode === "day"
            ? null
            : checkOutHour === ""
            ? null
            : Number(checkOutHour),

        // mirror overnight rules into bookingRules for HostCalendar consistency
        bookingRules: {
          minNights:
            bookingMode === "day"
              ? null
              : minNights === ""
              ? null
              : Number(minNights),
          maxNights:
            bookingMode === "day"
              ? null
              : maxNights === ""
              ? null
              : Number(maxNights),
        },

        // house rules legacy field
        rules: rules.trim(),

        // structured things-to-know (new)
        thingsToKnow: {
          houseRules: rules.trim() || null,
          safety: safetyNotes.trim() || null,
          notes: otherNotes.trim() || null,
          cancellation: {
            key: DEFAULT_CANCELLATION_POLICY.key,
            title: DEFAULT_CANCELLATION_POLICY.title,
            text: DEFAULT_CANCELLATION_POLICY.guestFacingText,
          },
        },

        // FIXED: Booking approval model - properly saved
        instantBook,
        // Approval window is fixed at 4 hours when not instant book
        approvalWindowHours: instantBook ? null : approvalWindowHours,

        // Derived guest-facing prices (safe to ignore downstream)
        guestDisplayDayPrice,
        guestDisplayNightlyPrice,

        // Pricing policy so downstream can compute consistently
        pricingPolicy: {
          markupRate: MARKUP_RATE,
          hostFeeRate: HOST_FEE_RATE,
          roundingStepDay: ROUND_STEP_DAY,
          roundingStepNight: ROUND_STEP_NIGHT,
          includesFeeInDisplay: true,
          guestSeesNoServiceFee: true,
        },

        // Normalized geocode info (optional)
        addressStreet1: finalGeo?.addressStreet1 ?? null,
        addressStreet2: finalGeo?.addressStreet2 ?? null,
        addressCounty: finalGeo?.addressCounty ?? null,
        formattedAddress: finalGeo?.formattedAddress ?? null,
        locationLat: finalGeo?.locationLat ?? null,
        locationLng: finalGeo?.locationLng ?? null,
        locationCitySlug: finalGeo?.locationCitySlug ?? null,
        locationMetroKey: finalGeo?.locationMetroKey ?? null,
        normalizedAddress: finalGeo || null,

        files: compressed,
        onProgress: (p) => setUploadProgress(Math.round(p)),
      };

      await publishListing(payload);

      localStorage.removeItem(DRAFT_KEY);
      navigate("/host/listings?submitted=1", { replace: true });
    } catch (err) {
      console.error(err);
      setGlobalMsg("Couldn't publish your listing. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /** -------- Hydration guard (avoid flash while auth loads) ---------- **/
  if (loading) {
    return (
      <div className="min-h-screen bg-white text-neutral-900">
        <main className="mx-auto max-w-3xl px-4 py-16">
          <div className="animate-pulse rounded-2xl border border-neutral-200 p-6">
            <div className="h-4 w-44 rounded bg-neutral-200" />
            <div className="mt-4 h-3 w-full rounded bg-neutral-200" />
            <div className="mt-2 h-3 w-5/6 rounded bg-neutral-200" />
          </div>
        </main>
      </div>
    );
  }
  if (!user) return null;

  /** ------------------------------- UI -------------------------------- **/
  const stepPercent = Math.round((step / TOTAL_STEPS) * 100);

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      {/* Sticky header */}
      <header className="sticky top-0 z-30 w-full border-b border-neutral-200 bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-end px-4 py-3">
          <div className="flex items-center gap-2">
            <VerificationBadges idStatus={idStatus} rtlStatus={rtlStatus} />
            <button
              onClick={saveDraftNow}
              className="rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:border-neutral-400"
            >
              Save draft
            </button>
            <button
              onClick={exitToHome}
              className="rounded-full px-3 py-1.5 text-sm hover:bg-neutral-100"
              title="Save draft & return home"
            >
              Exit
            </button>
            <button
              onClick={() => {
                const ok = window.confirm(
                  "Discard draft and return home? This cannot be undone."
                );
                if (ok) {
                  localStorage.removeItem(DRAFT_KEY);
                  navigate("/", { replace: true });
                }
              }}
              className="rounded-full px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
              title="Discard draft and return home"
            >
              Discard
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        {/* steps */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-semibold sm:text-3xl">Create your listing</h1>
            <div className="text-sm text-neutral-500">
              Step {step} of {TOTAL_STEPS} · {STEP_TITLES[step - 1]}
            </div>
          </div>
        </div>

        <div className="mb-6 h-1.5 w-full overflow-hidden rounded-full bg-neutral-200">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${stepPercent}%`, backgroundColor: BRAND }}
          />
        </div>

        {globalMsg && (
          <div className="mb-4 rounded-2xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-700">
            {globalMsg}
          </div>
        )}
        {fileWarning && (
          <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {fileWarning}
          </div>
        )}

        {/* Single-step cards */}
        <div className="mt-6 space-y-6">
          {/* Step 1: ID verification */}
          {step === 1 && (
            <Card>
              <div className="flex items-center gap-2">
                <H2>Verify your ID</H2>
                <InfoQ text="We verify hosts to keep guests safe. You can publish now; your listing stays pending until verification is approved." />
              </div>
              <AccentBar />

              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">Identity — ID + selfie</div>
                    <InfoQ text="A clear photo of your government ID (front), and a selfie so we can match the face. Cover sensitive numbers if you want; your name and photo must be visible." />
                  </div>
                  <StatusPill status={idStatus} />
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <UploadCell
                    label="ID (front)"
                    file={idFront}
                    onFile={setIdFront}
                    accept="image/*"
                    capture="user"
                  />
                  <UploadCell
                    label="ID (back, optional)"
                    file={idBack}
                    onFile={setIdBack}
                    accept="image/*"
                    capture="environment"
                  />
                  <UploadCell
                    label="Selfie"
                    file={selfie}
                    onFile={setSelfie}
                    accept="image/*"
                    capture="user"
                    help="Take a quick selfie in good lighting. No code or special pose required."
                  />
                </div>

                {idProgress > 0 && idProgress < 100 && (
                  <ProgressBar label="Uploading identity…" value={idProgress} />
                )}
                {idNote && idStatus === "rejected" && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {idNote}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    onClick={submitIdentity}
                    className="rounded-full px-5 py-2 text-sm font-semibold text-white hover:opacity-90"
                    style={{ backgroundColor: BRAND }}
                  >
                    {idStatus === "unverified" ? "Submit identity" : "Resubmit identity"}
                  </button>
                  <span className="text-xs text-neutral-500">
                    We will use this information to help verify your identity
                  </span>
                </div>
              </div>

              <WizardNav onBack={back} onNext={next} backDisabled />
            </Card>
          )}

          {/* Step 2: Right to list */}
          {step === 2 && (
            <Card>
              <div className="flex items-center gap-2">
                <H2>Right to list</H2>
                <InfoQ text="Upload a document showing you own the property or have permission to host." />
              </div>
              <AccentBar />

              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">Ownership or permission</div>
                    <InfoQ text="Examples: tax bill or mortgage (owner), business license/lease (business), or a lease page + signed consent (tenant)." />
                  </div>
                  <StatusPill status={rtlStatus} />
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <RadioPill
                    label="Owner"
                    checked={ownershipType === "owner"}
                    onChange={() => setOwnershipType("owner")}
                  />
                  <RadioPill
                    label="Tenant"
                    checked={ownershipType === "tenant"}
                    onChange={() => setOwnershipType("tenant")}
                  />
                  <RadioPill
                    label="Business"
                    checked={ownershipType === "business"}
                    onChange={() => setOwnershipType("business")}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <UploadCell
                    label={
                      ownershipType === "tenant"
                        ? "Signed consent (PM/landlord)"
                        : ownershipType === "business"
                        ? "Business doc (license/lease/utility)"
                        : "Property doc (tax bill/mortgage/assessor)"
                    }
                    file={rtlDoc}
                    onFile={setRtlDoc}
                    accept="image/*,application/pdf"
                  />
                  {ownershipType === "tenant" && (
                    <UploadCell
                      label="Lease page with your name"
                      file={landlordConsent}
                      onFile={setLandlordConsent}
                      accept="image/*,application/pdf"
                    />
                  )}
                </div>

                {rtlProgress > 0 && rtlProgress < 100 && (
                  <ProgressBar label="Uploading right-to-list…" value={rtlProgress} />
                )}
                {rtlNote && rtlStatus === "rejected" && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {rtlNote}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    onClick={submitRightToListDocs}
                    className="rounded-full px-5 py-2 text-sm font-semibold text-white hover:opacity-90"
                    style={{ backgroundColor: BRAND }}
                  >
                    {rtlStatus === "unverified" ? "Submit documents" : "Resubmit documents"}
                  </button>
                  <span className="text-xs text-neutral-500">
                    You can blur account numbers; keep your name and address visible.
                  </span>
                </div>
              </div>

              <WizardNav onBack={back} onNext={next} />
            </Card>
          )}

          {/* Step 3: Title & summary */}
          {step === 3 && (
            <Card>
              <H2>Title & summary</H2>
              <AccentBar />
              <div className="mt-5 grid gap-5">
                <Field label="Listing title" error={errors.title}>
                  <input
                    ref={titleRef}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                    placeholder="Sunlit home with garden patio"
                  />
                </Field>
                <Field label="Short description" error={errors.summary}>
                  <textarea
                    ref={summaryRef}
                    rows={4}
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                    placeholder="Why your space works for gatherings and/or overnight stays."
                  />
                </Field>
              </div>
              <WizardNav onBack={back} onNext={next} />
            </Card>
          )}

          {/* Step 4: Space details */}
          {step === 4 && (
            <Card>
              <H2>Space details</H2>
              <AccentBar />
              <div className="mt-5 grid gap-5">
                <div className="space-y-4 rounded-2xl bg-neutral-50 p-4">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">What part of your property is this?</div>
                    <InfoQ text="List an entire place, a private room, a shared room, or just your backyard." />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {SPACE_TYPES.map((t) => (
                      <RadioPill
                        key={t.value}
                        label={t.label}
                        checked={spaceType === t.value}
                        onChange={() => setSpaceType(t.value)}
                      />
                    ))}
                  </div>

                  {spaceType !== "entire_place" && (
                    <div>
                      <Field
                        label="Space name (e.g., 'Blue Room')"
                        error={errors.spaceName}
                      >
                        <input
                          ref={spaceNameRef}
                          value={spaceName}
                          onChange={(e) => setSpaceName(e.target.value)}
                          className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                          placeholder="Name shown to guests"
                        />
                      </Field>
                    </div>
                  )}

                  <Field label="Property type" error={errors.propertyType}>
                    <select
                      name="propertyType"
                      value={propertyType}
                      onChange={(e) => setPropertyType(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-neutral-300 bg-white px-4 py-3 text-sm outline-none focus:border-neutral-400"
                    >
                      <option value="">Select property type</option>
                      {PROPERTY_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <div>
                    <div className="text-sm font-medium">Entrance</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {ENTRANCE_TYPES.map((t) => (
                        <RadioPill
                          key={t.value}
                          label={t.label}
                          checked={entranceType === t.value}
                          onChange={() => setEntranceType(t.value)}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium">
                      Areas included / accessible (optional)
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {AREA_CHOICES.map((name) => (
                        <ToggleChip
                          key={name}
                          label={name}
                          checked={areas.includes(name)}
                          onToggle={() =>
                            setAreas((prev) =>
                              prev.includes(name)
                                ? prev.filter((x) => x !== name)
                                : [...prev, name]
                            )
                          }
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <WizardNav onBack={back} onNext={next} />
            </Card>
          )}

          {/* Step 5: Capacity & beds */}
          {step === 5 && (
            <Card>
              <H2>Capacity & beds</H2>
              <AccentBar />
              <p className="mt-3 text-sm text-neutral-600">
                For You're Welcome, we separate event capacity (daytime gatherings) from sleeping capacity (overnight stays).
              </p>

              <div className="mt-5 grid gap-5">
                <div className="rounded-2xl bg-neutral-50 p-4">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">Capacity</div>
                    <InfoQ text="A house might host 30 for a baby shower but only sleep 8 overnight." />
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Max for events (daytime)" error={errors.maxGuestsDay}>
                      <input
                        ref={maxGuestsDayRef}
                        type="number"
                        min={1}
                        value={maxGuestsDay}
                        onChange={(e) => setMaxGuestsDay(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                        placeholder="e.g., 30"
                      />
                      <div className="mt-1 text-xs text-neutral-500">
                        Showers, dinners, offsites
                      </div>
                    </Field>
                    <Field label="Max for overnight stays" error={errors.maxGuestsOvernight}>
                      <input
                        ref={maxGuestsOvernightRef}
                        type="number"
                        min={1}
                        value={maxGuestsOvernight}
                        onChange={(e) => setMaxGuestsOvernight(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                        placeholder="e.g., 8"
                      />
                      <div className="mt-1 text-xs text-neutral-500">
                        Sleeping capacity
                      </div>
                    </Field>
                  </div>
                </div>

                <div className="rounded-2xl bg-neutral-50 p-4">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">Bedrooms & bathrooms</div>
                    <InfoQ text="Table-stakes for any stay/gathering product. We'll add per-room bed types later." />
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Field label="Bedrooms" error={errors.bedrooms}>
                      <input
                        ref={bedroomsRef}
                        type="number"
                        min={0}
                        value={bedrooms}
                        onChange={(e) => setBedrooms(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                        placeholder="e.g., 3"
                      />
                    </Field>
                    <Field label="Total beds" error={errors.beds}>
                      <input
                        ref={bedsRef}
                        type="number"
                        min={1}
                        value={beds}
                        onChange={(e) => setBeds(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                        placeholder="e.g., 4"
                      />
                    </Field>
                    <Field label="Bathrooms" error={errors.bathrooms}>
                      <input
                        ref={bathroomsRef}
                        type="number"
                        min={0.5}
                        step={0.5}
                        value={bathrooms}
                        onChange={(e) => setBathrooms(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                        placeholder="e.g., 2.5"
                      />
                      <div className="mt-1 text-xs text-neutral-500">
                        Can be decimal (e.g., 1.5)
                      </div>
                    </Field>
                  </div>
                </div>
              </div>
              <WizardNav onBack={back} onNext={next} />
            </Card>
          )}

          {/* Step 6: Location */}
          {step === 6 && (
            <Card>
              <H2>Location</H2>
              <AccentBar />
              <div className="mt-5 grid gap-5">
                <Field label="Street address" error={errors.address}>
                  <input
                    ref={addressRef}
                    value={address}
                    onChange={(e) => {
                      setAddress(e.target.value);
                      setGeocodingStatus("idle");
                    }}
                    className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                    placeholder="Start typing and pick a suggestion…"
                  />
                  <div className="mt-1 flex items-center justify-between text-[11px] text-neutral-500">
                    <span>Exact address is only shared after a booking is confirmed.</span>
                    <span>Suggestions powered by Google.</span>
                  </div>
                  {addressLoading && (
                    <p className="mt-1 text-xs text-neutral-500">
                      Looking up suggestions…
                    </p>
                  )}
                  {addressAutocompleteError && (
                    <p className="mt-1 text-xs text-red-600">
                      {addressAutocompleteError}
                    </p>
                  )}
                  {geocodingStatus === "ok" && (
                    <p className="mt-1 text-xs text-emerald-600">
                      Address verified.
                    </p>
                  )}
                  {geocodingStatus === "error" && (
                    <p className="mt-1 text-xs text-amber-700">
                      We couldn't verify this address yet. You can still publish; we'll use what you typed.
                    </p>
                  )}
                </Field>

                {!!addressSuggestions.length && (
                  <div className="rounded-2xl border border-neutral-200 bg-white text-sm shadow-sm">
                    <ul className="max-h-60 overflow-y-auto">
                      {addressSuggestions.map((p) => (
                        <li key={p.placeId}>
                          <button
                            type="button"
                            onClick={() => handleAddressSuggestionClick(p)}
                            className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-neutral-50"
                          >
                            <span className="font-medium">
                              {p.mainText || p.description}
                            </span>
                            {p.secondaryText && (
                              <span className="text-xs text-neutral-500">
                                {p.secondaryText}
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                  <Field label="City" className="sm:col-span-2" error={errors.city}>
                    <input
                      ref={cityRef}
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        setGeocodingStatus("idle");
                      }}
                      className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                      placeholder="e.g., St. Louis"
                    />
                  </Field>
                  <Field label="State (2-letter)" error={errors.stateCode}>
                    <input
                      ref={stateRef}
                      value={stateCode}
                      onChange={(e) => {
                        setStateCode(e.target.value.toUpperCase().slice(0, 2));
                        setGeocodingStatus("idle");
                      }}
                      className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm uppercase outline-none focus:border-neutral-400"
                      placeholder="MO"
                      maxLength={2}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                  <Field label="Postal code" error={errors.postalCode}>
                    <input
                      ref={postalCodeRef}
                      value={postalCode}
                      onChange={(e) => {
                        setPostalCode(e.target.value);
                        setGeocodingStatus("idle");
                      }}
                      className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                      placeholder="e.g., 63118"
                    />
                  </Field>
                  <Field label="Country" className="sm:col-span-2">
                    <select
                      value={countryCode}
                      onChange={(e) => {
                        setCountryCode(e.target.value || "US");
                        setGeocodingStatus("idle");
                      }}
                      className="mt-1 w-full rounded-2xl border border-neutral-300 bg-white px-4 py-3 text-sm outline-none focus:border-neutral-400"
                    >
                      <option value="US">United States</option>
                    </select>
                  </Field>
                </div>
              </div>
              <WizardNav onBack={back} onNext={next} />
            </Card>
          )}

          {/* Step 7: Amenities */}
          {step === 7 && (
            <Card>
              <H2>Amenities</H2>
              <AccentBar />
              <div className="mt-5 rounded-2xl bg-neutral-50 p-4">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium">What can guests use?</div>
                  <InfoQ text="Select everything guests can use. Safety devices should be accurate. You can update anytime." />
                </div>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      value={amenityQuery}
                      onChange={(e) => setAmenityQuery(e.target.value)}
                      placeholder="Search amenities…"
                      className="w-full rounded-2xl border border-neutral-300 px-4 py-2.5 text-sm outline-none focus:border-neutral-400 sm:w-64"
                    />
                    {amenityQuery && (
                      <button
                        type="button"
                        onClick={() => setAmenityQuery("")}
                        className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:border-neutral-400"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        addAmenities([
                          "wifi",
                          "tv",
                          "heating",
                          "ac",
                          "washer",
                          "dryer",
                          "workspace",
                          "kitchen",
                          "refrigerator",
                          "microwave",
                          "coffee_maker",
                          "cooking_basics",
                          "smoke_alarm",
                          "co_alarm",
                          "first_aid",
                          "fire_extinguisher",
                        ])
                      }
                      className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:border-neutral-400"
                      title="Add a common starter set"
                    >
                      Select recommended
                    </button>
                    <button
                      type="button"
                      onClick={clearAmenities}
                      className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:border-neutral-400"
                    >
                      Clear all
                    </button>
                    <span className="text-xs text-neutral-500">
                      {amenities.length} selected
                    </span>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  {AMENITY_GROUPS.map(({ group, items }) => {
                    const filtered = items.filter(
                      (i) =>
                        !amenityQuery ||
                        i.label.toLowerCase().includes(amenityQuery.toLowerCase())
                    );
                    if (!filtered.length) return null;
                    return (
                      <div key={group} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                            {group}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => addAmenities(filtered.map((f) => f.key))}
                              className="rounded-full border border-neutral-300 px-2.5 py-1 text-xs hover:border-neutral-400"
                            >
                              Select group
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setAmenities((prev) =>
                                  prev.filter(
                                    (k) =>
                                      !filtered.some(
                                        (f) => normalizeAmenityKey(f.key) === k
                                      )
                                  )
                                )
                              }
                              className="rounded-full border border-neutral-300 px-2.5 py-1 text-xs hover:border-neutral-400"
                            >
                              Deselect group
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                          {filtered.map((i) => (
                            <AmenityChip
                              key={i.key}
                              label={i.label}
                              name={i.key}
                              selected={amenities.includes(normalizeAmenityKey(i.key))}
                              onToggle={() => toggleAmenity(i.key)}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <WizardNav onBack={back} onNext={next} />
            </Card>
          )}

          {/* Step 8: Neighbors & noise */}
          {step === 8 && (
            <Card>
              <H2>Neighbors & noise</H2>
              <AccentBar />
              <p className="mt-3 text-sm text-neutral-600">
                Help guests understand what events work best and how to be good neighbors.
              </p>

              <div className="mt-5 space-y-6">
                {/* Parking specifics */}
                <div className="rounded-2xl bg-neutral-50 p-4">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">Parking specifics</div>
                    <InfoQ text="Not just 'Free parking' - guests always ask 'how many cars?'" />
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4">
                    <Field label="Onsite parking spots (optional)">
                      <input
                        ref={parkingSpotsRef}
                        type="number"
                        min={0}
                        value={parkingSpotsOnsite}
                        onChange={(e) => setParkingSpotsOnsite(e.target.value)}
                        className="mt-1 w-40 rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                        placeholder="e.g., 3"
                      />
                    </Field>
                    <Field label="Parking notes (optional)">
                      <textarea
                        ref={parkingNotesRef}
                        rows={2}
                        value={parkingNotes}
                        onChange={(e) => setParkingNotes(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                        placeholder="Driveway fits 3 cars; street parking free on weekends. No RV parking."
                      />
                    </Field>
                  </div>
                </div>

                {/* Noise & neighbors */}
                <div className="rounded-2xl bg-neutral-50 p-4">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">Noise & neighbors</div>
                    <InfoQ text="Clear expectations help avoid neighbor complaints and guest disappointments." />
                  </div>
                  <div className="mt-4 space-y-4">
                    <div>
                      <div className="text-sm font-medium">Suitable for loud music?</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <RadioPill
                          label="Yes"
                          checked={suitableForLoudMusic === true}
                          onChange={() => setSuitableForLoudMusic(true)}
                        />
                        <RadioPill
                          label="No"
                          checked={suitableForLoudMusic === false}
                          onChange={() => setSuitableForLoudMusic(false)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="Quiet hours start (optional)" error={errors.quietHoursStart}>
                        <TimeSelect
                          refEl={quietHoursStartRef}
                          value={quietHoursStart}
                          onChange={setQuietHoursStart}
                          placeholder="No quiet hours"
                          maxHour={23}
                        />
                      </Field>
                      <Field label="Quiet hours end (optional)" error={errors.quietHoursEnd}>
                        <TimeSelect
                          refEl={quietHoursEndRef}
                          value={quietHoursEnd}
                          onChange={setQuietHoursEnd}
                          placeholder="No quiet hours"
                          maxHour={23}
                        />
                      </Field>
                    </div>

                    <div>
                      <div className="text-sm font-medium">Shared walls with neighbors?</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <RadioPill
                          label="Yes"
                          checked={sharedWalls === true}
                          onChange={() => setSharedWalls(true)}
                        />
                        <RadioPill
                          label="No"
                          checked={sharedWalls === false}
                          onChange={() => setSharedWalls(false)}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium">Good for these event types</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {EVENT_TYPES.map((type) => (
                          <ToggleChip
                            key={type}
                            label={type}
                            checked={goodForEventsTypes.includes(type)}
                            onToggle={() =>
                              setGoodForEventsTypes((prev) =>
                                prev.includes(type)
                                  ? prev.filter((x) => x !== type)
                                  : [...prev, type]
                              )
                            }
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Kids & pets */}
                <div className="rounded-2xl bg-neutral-50 p-4">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">Kids & pets</div>
                    <InfoQ text="Families need to know if your space works for them." />
                  </div>
                  <div className="mt-4 space-y-4">
                    <Field label="Suitable for children">
                      <select
                        value={suitableForChildren}
                        onChange={(e) => setSuitableForChildren(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-neutral-300 bg-white px-4 py-3 text-sm outline-none focus:border-neutral-400"
                      >
                        <option value="">Select option</option>
                        {CHILDREN_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <div>
                      <div className="text-sm font-medium">Suitable for infants (under 2)?</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <RadioPill
                          label="Yes"
                          checked={suitableForInfants === true}
                          onChange={() => setSuitableForInfants(true)}
                        />
                        <RadioPill
                          label="No"
                          checked={suitableForInfants === false}
                          onChange={() => setSuitableForInfants(false)}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium">Pets allowed?</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <RadioPill
                          label="Yes"
                          checked={petsAllowed === true}
                          onChange={() => setPetsAllowed(true)}
                        />
                        <RadioPill
                          label="No"
                          checked={petsAllowed === false}
                          onChange={() => setPetsAllowed(false)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Host presence */}
                <div className="rounded-2xl bg-neutral-50 p-4">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">Host presence</div>
                    <InfoQ text="Guests really care if they're sharing space with the host." />
                  </div>
                  <div className="mt-4 space-y-4">
                    <Field label="Do you live onsite?">
                      <select
                        value={hostLivesOnsite}
                        onChange={(e) => setHostLivesOnsite(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-neutral-300 bg-white px-4 py-3 text-sm outline-none focus:border-neutral-400"
                      >
                        <option value="">Select option</option>
                        {HOST_PRESENCE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <div>
                      <div className="text-sm font-medium">Is this your primary residence?</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <RadioPill
                          label="Yes"
                          checked={isPrimaryResidence === true}
                          onChange={() => setIsPrimaryResidence(true)}
                        />
                        <RadioPill
                          label="No"
                          checked={isPrimaryResidence === false}
                          onChange={() => setIsPrimaryResidence(false)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <WizardNav onBack={back} onNext={next} />
            </Card>
          )}

          {/* Step 9: Photos */}
          {step === 9 && (
            <Card>
              <H2>Photos</H2>
              <AccentBar />
              <p className="mt-3 text-sm text-neutral-600">
                Add at least one photo. Use bright, natural light. We'll compress on upload
                to save you money.
              </p>

              <div
                className={`mt-5 rounded-2xl border border-dashed p-8 text-center transition ${
                  dragging
                    ? "border-neutral-600 bg-neutral-50"
                    : "border-neutral-300 hover:border-neutral-400"
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
                }}
                aria-label="Upload photos"
              >
                <div
                  className="mx-auto h-12 w-12 rounded-2xl"
                  style={{ backgroundColor: BRAND }}
                />
                <p className="mt-3 text-sm">
                  <span className="font-semibold">Drag & drop</span> your images here or{" "}
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="underline"
                  >
                    browse files
                  </button>
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  JPEG/PNG/WebP up to ~{MAX_FILE_MB}MB each • {files.length}/{MAX_FILES}{" "}
                  selected
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPTED_TYPES.join(",")}
                  multiple
                  className="hidden"
                  onChange={handleInputChange}
                />
                {errors.photos && (
                  <p className="mt-2 text-xs text-red-600">{errors.photos}</p>
                )}
              </div>

              {!!previews.length && (
                <>
                  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {previews.map((src, i) => (
                      <div
                        key={src}
                        className="group relative overflow-hidden rounded-2xl border border-neutral-200"
                      >
                        <img
                          src={src}
                          alt={`preview-${i}`}
                          className="h-40 w-full object-cover"
                        />
                        <div className="absolute inset-x-2 top-2 flex justify-between gap-2">
                          <button
                            onClick={() => movePhoto(i, -1)}
                            aria-label="Move earlier"
                            className="rounded-full bg-white/90 px-2 py-1 text-xs shadow hover:bg-white disabled:opacity-40"
                            disabled={i === 0}
                            title="Move left"
                          >
                            ←
                          </button>
                          <button
                            onClick={() => removePhoto(i)}
                            aria-label="Remove photo"
                            className="rounded-full bg-white/90 px-2 py-1 text-xs shadow hover:bg-white"
                            title="Remove"
                          >
                            ×
                          </button>
                          <button
                            onClick={() => movePhoto(i, +1)}
                            aria-label="Move later"
                            className="rounded-full bg-white/90 px-2 py-1 text-xs shadow hover:bg-white disabled:opacity-40"
                            disabled={i === previews.length - 1}
                            title="Move right"
                          >
                            →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <button
                      onClick={clearPhotos}
                      className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:border-neutral-400"
                    >
                      Remove all photos
                    </button>
                  </div>
                </>
              )}

              <WizardNav onBack={back} onNext={next} />
            </Card>
          )}

          {/* Step 10: Usage & Pricing */}
          {step === 10 && (
            <Card>
              <H2>Usage & pricing</H2>
              <AccentBar />

              {/* Booking mode selector */}
              <div className="mt-5 space-y-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">
                    How can guests book your place?
                  </label>
                  <InfoQ text="Daytime = single calendar day, no sleeping. Overnight = multi-night stay. Day + Overnight supports both." />
                </div>
                <div className="flex flex-wrap gap-2">
                  <RadioPill
                    label="Daytime only"
                    checked={bookingMode === "day"}
                    onChange={() => setBookingMode("day")}
                  />
                  <RadioPill
                    label="Overnight"
                    checked={bookingMode === "overnight"}
                    onChange={() => setBookingMode("overnight")}
                  />
                  <RadioPill
                    label="Day + Overnight"
                    checked={bookingMode === "hybrid"}
                    onChange={() => setBookingMode("hybrid")}
                  />
                </div>
              </div>

              {/* Approval model - FIXED: No editable approval window */}
              <div className="mt-6 rounded-2xl bg-neutral-50 p-4">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium">Booking approvals</div>
                  <InfoQ text="Instant Book auto-accepts bookings; Request to Book lets you approve first (4-hour response window)." />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <RadioPill
                    label="Instant Book (recommended)"
                    checked={instantBook}
                    onChange={() => setInstantBook(true)}
                  />
                  <RadioPill
                    label="Request to Book"
                    checked={!instantBook}
                    onChange={() => setInstantBook(false)}
                  />
                </div>

                {!instantBook && (
                  <div className="mt-3 rounded-2xl border border-neutral-200 bg-white px-4 py-3">
                    <div className="text-sm font-medium">Response window</div>
                    <div className="mt-1 text-sm text-neutral-600">
                      You'll have <strong>4 hours</strong> to respond before the request expires.
                      Most guests expect a reply within <strong>1 hour</strong>.
                    </div>
                  </div>
                )}
              </div>

              {/* Daytime pricing */}
              {(bookingMode === "day" || bookingMode === "hybrid") && (
                <div className="mt-6 rounded-2xl bg-neutral-50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">Daytime pricing</div>
                  </div>
                  <div className="mt-4 space-y-4">
                    <Field
                      label="Your base day rate (before our fee)"
                      error={errors.dayPrice}
                    >
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="rounded-2xl border border-neutral-300 px-3 py-2.5 text-sm">
                          $
                        </span>
                        <input
                          ref={priceRef}
                          type="number"
                          min={1}
                          value={dayPrice}
                          onChange={(e) => setDayPrice(e.target.value)}
                          className="w-40 rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                          placeholder={suggestedPrice ? `${suggestedPrice}` : "e.g., 300"}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (suggestedPrice) setDayPrice(String(suggestedPrice));
                            else {
                              const c = Number(maxGuestsDay || maxGuestsOvernight || 0);
                              if (!city || !stateCode || !c) return;
                              const s = suggestDayRate({ city, stateCode, capacity: c });
                              setDayPrice(String(s));
                            }
                          }}
                          className="rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:border-neutral-400"
                          title="Use suggested price"
                        >
                          Suggest price
                        </button>
                      </div>
                      <div className="mt-2 text-xs text-neutral-600">
                        Guests will see ≈{" "}
                        <strong>{money0(guestDayDisplay)}</strong>/day{" "}
                        <span className="text-neutral-500">
                          (includes platform fee; taxes added at checkout)
                        </span>
                      </div>
                      <div className="text-xs text-neutral-500">
                        Your payout ≈ <strong>{money2(hostDayPayout)}</strong>/day (
                        {HOST_FEE_RATE_EFFECTIVE === 0
                          ? `0% host fee promo (first ${HOST_FEE_PROMO_MONTHS} months)`
                          : `after ${Math.round(HOST_FEE_RATE * 100)}% host fee`}
                        , before payment processing).
                      </div>
                    </Field>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field
                        label="Day-use start time (optional)"
                        error={errors.dayUseStartHour}
                      >
                        <TimeSelect
                          refEl={dayStartRef}
                          value={dayUseStartHour}
                          onChange={setDayUseStartHour}
                          placeholder="No start time set"
                          maxHour={23}
                        />
                      </Field>
                      <Field
                        label="Day-use end time (optional)"
                        error={errors.dayUseEndHour}
                      >
                        <TimeSelect
                          refEl={dayEndRef}
                          value={dayUseEndHour}
                          onChange={setDayUseEndHour}
                          placeholder="No end time set"
                          maxHour={24}
                        />
                      </Field>
                    </div>
                  </div>
                </div>
              )}

              {/* Overnight pricing */}
              {(bookingMode === "overnight" || bookingMode === "hybrid") && (
                <div className="mt-6 rounded-2xl bg-neutral-50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">Overnight pricing & rules</div>
                  </div>
                  <div className="mt-4 space-y-4">
                    <Field
                      label="Your base nightly rate (before our fee)"
                      error={errors.nightlyPrice}
                    >
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="rounded-2xl border border-neutral-300 px-3 py-2.5 text-sm">
                          $
                        </span>
                        <input
                          ref={nightlyRef}
                          type="number"
                          min={1}
                          value={nightlyPrice}
                          onChange={(e) => setNightlyPrice(e.target.value)}
                          className="w-40 rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                          placeholder={
                            suggestedNightly ? `${suggestedNightly}` : "e.g., 240"
                          }
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (suggestedNightly)
                              setNightlyPrice(String(suggestedNightly));
                            else if (suggestedPrice)
                              setNightlyPrice(
                                String(Math.max(50, Math.round(suggestedPrice * 0.8)))
                              );
                          }}
                          className="rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:border-neutral-400"
                          title="Use suggested nightly"
                        >
                          Suggest nightly
                        </button>
                      </div>
                      <div className="mt-2 text-xs text-neutral-600">
                        Guests will see ≈{" "}
                        <strong>{money0(guestNightDisplay)}</strong>/night{" "}
                        <span className="text-neutral-500">
                          (includes platform fee; taxes added at checkout)
                        </span>
                      </div>
                      <div className="text-xs text-neutral-500">
                        Your payout ≈ <strong>{money2(hostNightPayout)}</strong>/night (
                        {HOST_FEE_RATE_EFFECTIVE === 0
                          ? `0% host fee promo (first ${HOST_FEE_PROMO_MONTHS} months)`
                          : `after ${Math.round(HOST_FEE_RATE * 100)}% host fee`}
                        , before payment processing).
                      </div>
                    </Field>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field label="Min nights" error={errors.minNights}>
                        <input
                          ref={minNightsRef}
                          type="number"
                          min={1}
                          value={minNights}
                          onChange={(e) => setMinNights(e.target.value)}
                          className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                          placeholder="e.g., 1"
                        />
                      </Field>
                      <Field
                        label="Max nights (optional)"
                        error={errors.maxNights}
                      >
                        <input
                          ref={maxNightsRef}
                          type="number"
                          min={1}
                          value={maxNights}
                          onChange={(e) => setMaxNights(e.target.value)}
                          className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                          placeholder="e.g., 14"
                        />
                      </Field>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field
                        label="Check-in time (optional)"
                        error={errors.checkInHour}
                      >
                        <TimeSelect
                          refEl={checkInRef}
                          value={checkInHour}
                          onChange={setCheckInHour}
                          placeholder="No set time"
                          maxHour={23}
                        />
                      </Field>
                      <Field
                        label="Check-out time (optional)"
                        error={errors.checkOutHour}
                      >
                        <TimeSelect
                          refEl={checkOutRef}
                          value={checkOutHour}
                          onChange={setCheckOutHour}
                          placeholder="No set time"
                          maxHour={23}
                        />
                      </Field>
                    </div>
                  </div>
                </div>
              )}

              <WizardNav onBack={back} onNext={next} />
            </Card>
          )}

          {/* Step 11: Things to know */}
          {step === 11 && (
            <Card>
              <H2>Things to know</H2>
              <AccentBar />
              <p className="mt-3 text-sm text-neutral-600">
                Guests see this near the bottom of your listing, similar to Airbnb's
                "Things to know" section.
              </p>

              <div className="mt-5 grid gap-5">
                <Field label="House rules (optional)">
                  <textarea
                    rows={4}
                    value={rules}
                    onChange={(e) => setRules(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                    placeholder="Quiet hours, no amplified music after 10pm, parking plan, smoking rules, etc."
                  />
                </Field>
                <Field label="Safety & property (optional)">
                  <textarea
                    rows={3}
                    value={safetyNotes}
                    onChange={(e) => setSafetyNotes(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                    placeholder="Cameras at entrance, stairs, noise-sensitive neighbors, shared spaces, etc."
                  />
                </Field>
                <Field label="Additional notes (optional)">
                  <textarea
                    rows={3}
                    value={otherNotes}
                    onChange={(e) => setOtherNotes(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-400"
                    placeholder="Anything else guests should know before booking."
                  />
                </Field>
              </div>

              {/* Read-only cancellation policy */}
              <div className="mt-6 rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium">
                      {DEFAULT_CANCELLATION_POLICY.title}
                    </div>
                    <div className="mt-1 text-xs text-neutral-500">
                      Applies automatically to all bookings on {BRAND_NAME}. Hosts can't change this policy.
                    </div>
                  </div>
                  <Link
                    to="/cancellation-policy"
                    className="text-xs font-medium text-neutral-700 underline underline-offset-2 hover:text-neutral-900"
                  >
                    View full policy
                  </Link>
                </div>

                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-neutral-700">
                  {(DEFAULT_CANCELLATION_POLICY.guestFacingPoints || []).map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>

                <div className="mt-3 text-xs text-neutral-500">
                  This is a summary. For full details, see the{" "}
                  <Link
                    to="/cancellation-policy"
                    className="underline underline-offset-2 hover:text-neutral-900"
                  >
                    Guest Cancellation &amp; Refund Policy
                  </Link>
                  .
                </div>
              </div>

              <WizardNav onBack={back} onNext={next} />
            </Card>
          )}

          {/* Step 12: Review & preview */}
          {step === 12 && (
            <Card>
              <H2>Review & preview</H2>
              <AccentBar />
              <div className="mt-5 grid gap-4">
                <Row label="Verification">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill status={idStatus} label="Identity" />
                    <StatusPill status={rtlStatus} label="Right to list" />
                  </div>
                </Row>
                <Row label="Title" value={title || "—"} />
                <Row label="Summary" value={summary || "—"} />
                <Row label="Event capacity (day)" value={`${maxGuestsDay || "—"} guests`} />
                <Row label="Sleeping capacity (night)" value={`${maxGuestsOvernight || "—"} guests`} />
                <Row label="Property type" value={PROPERTY_TYPES.find(p => p.value === propertyType)?.label || "—"} />
                <Row label="Bedrooms" value={bedrooms || "—"} />
                <Row label="Beds" value={beds || "—"} />
                <Row label="Bathrooms" value={bathrooms || "—"} />
                <Row
                  label="Location"
                  value={
                    [address, city, stateCode, postalCode].filter(Boolean).join(", ") ||
                    "—"
                  }
                />
                <Row
                  label="Space type"
                  value={
                    SPACE_TYPES.find((x) => x.value === spaceType)?.label || "—"
                  }
                />
                {spaceType !== "entire_place" && (
                  <Row label="Space name" value={spaceName || "—"} />
                )}
                <Row
                  label="Entrance"
                  value={
                    ENTRANCE_TYPES.find((x) => x.value === entranceType)?.label ||
                    "—"
                  }
                />

                {/* Parking */}
                {parkingSpotsOnsite && (
                  <Row label="Parking spots" value={`${parkingSpotsOnsite} onsite`} />
                )}
                {parkingNotes && <Row label="Parking notes" value={parkingNotes} />}

                {/* Noise & events */}
                {suitableForLoudMusic !== null && (
                  <Row label="Loud music OK" value={suitableForLoudMusic ? "Yes" : "No"} />
                )}
                {quietHoursStart && quietHoursEnd && (
                  <Row label="Quiet hours" value={`${hourLabel(parseInt(quietHoursStart))} to ${hourLabel(parseInt(quietHoursEnd))}`} />
                )}
                {sharedWalls !== null && (
                  <Row label="Shared walls" value={sharedWalls ? "Yes" : "No"} />
                )}
                {goodForEventsTypes.length > 0 && (
                  <Row label="Good for events">
                    <div className="flex flex-wrap gap-1">
                      {goodForEventsTypes.map((type) => (
                        <span
                          key={type}
                          className="rounded-full border border-neutral-300 px-2 py-0.5 text-xs"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </Row>
                )}

                {/* Kids & pets */}
                {suitableForChildren && (
                  <Row label="Children" value={CHILDREN_OPTIONS.find(c => c.value === suitableForChildren)?.label || "—"} />
                )}
                {suitableForInfants !== null && (
                  <Row label="Infants OK" value={suitableForInfants ? "Yes" : "No"} />
                )}
                {petsAllowed !== null && (
                  <Row label="Pets allowed" value={petsAllowed ? "Yes" : "No"} />
                )}

                {/* Host presence */}
                {hostLivesOnsite && (
                  <Row label="Host onsite" value={HOST_PRESENCE_OPTIONS.find(h => h.value === hostLivesOnsite)?.label || "—"} />
                )}
                {isPrimaryResidence !== null && (
                  <Row label="Primary residence" value={isPrimaryResidence ? "Yes" : "No"} />
                )}

                {areas?.length ? (
                  <Row label="Areas">
                    <div className="flex flex-wrap gap-1">
                      {areas.map((a) => (
                        <span
                          key={a}
                          className="rounded-full border border-neutral-300 px-2 py-0.5 text-xs"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                  </Row>
                ) : null}

                <Row label="Amenities">
                  {amenities.length ? (
                    <div className="flex flex-wrap gap-2">
                      {amenities.map((k) => {
                        const label =
                          AMENITY_GROUPS.flatMap((g) => g.items).find(
                            (x) => normalizeAmenityKey(x.key) === k
                          )?.label || k;
                        const Icon = ICONS[k] || ICONS._default;
                        return (
                          <span
                            key={k}
                            className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-2 py-1 text-xs"
                          >
                            <Icon selected />
                            <span className="pr-1">{label}</span>
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-neutral-500">—</div>
                  )}
                </Row>

                <Row label="Booking mode" value={BOOKING_MODE_LABEL[bookingMode]} />
                <Row
                  label="Approvals"
                  value={
                    instantBook
                      ? "Instant Book (auto-accept & charge)"
                      : "Request to Book (4-hour response window)"
                  }
                />

                {(bookingMode === "day" || bookingMode === "hybrid") && (
                  <>
                    <Row label="Base day rate (you set)">
                      {money2(Number(dayPrice || 0))}/day
                    </Row>
                    <Row label="Guests see (includes fee)">
                      {money0(guestDayDisplay)}/day
                    </Row>
                    <Row
                      label={
                        HOST_FEE_RATE_EFFECTIVE === 0
                          ? `Your payout est. (0% host fee promo — first ${HOST_FEE_PROMO_MONTHS} months)`
                          : `Your payout est. (after ${Math.round(HOST_FEE_RATE * 100)}%)`
                      }
                    >
                      {money2(hostDayPayout)}/day
                    </Row>
                  </>
                )}

                {(bookingMode === "overnight" || bookingMode === "hybrid") && (
                  <>
                    <Row label="Base nightly rate (you set)">
                      {money2(Number(nightlyPrice || 0))}/night
                    </Row>
                    <Row label="Guests see (includes fee)">
                      {money0(guestNightDisplay)}/night
                    </Row>
                    <Row
                      label={
                        HOST_FEE_RATE_EFFECTIVE === 0
                          ? `Your payout est. (0% host fee promo — first ${HOST_FEE_PROMO_MONTHS} months)`
                          : `Your payout est. (after ${Math.round(HOST_FEE_RATE * 100)}%)`
                      }
                    >
                      {money2(hostNightPayout)}/night
                    </Row>
                  </>
                )}

                <Row label="House rules" value={rules || "—"} />
                <Row label="Safety & property" value={safetyNotes || "—"} />
                <Row label="Additional notes" value={otherNotes || "—"} />

                <Row label="Cancellation">
                  <div className="text-sm">
                    <span className="font-medium">
                      {DEFAULT_CANCELLATION_POLICY.title}
                    </span>
                    {" — "}
                    {DEFAULT_CANCELLATION_POLICY.guestFacingText}
                  </div>
                </Row>

                {!!previews.length && (
                  <div>
                    <div className="text-sm text-neutral-500">Photos</div>
                    <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {previews.map((src) => (
                        <img
                          key={src}
                          src={src}
                          alt="review"
                          className="h-36 w-full rounded-2xl object-cover"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* In-page preview card */}
                <div className="mt-6">
                  <div className="mb-2 text-sm font-medium text-neutral-800">
                    Guest preview
                  </div>
                  <LivePreviewCard
                    title={title}
                    city={city}
                    stateCode={stateCode}
                    priceDay={bookingMode !== "overnight" ? guestDayDisplay : null}
                    priceNight={bookingMode !== "day" ? guestNightDisplay : null}
                    spaceType={
                      SPACE_TYPES.find((x) => x.value === spaceType)?.label
                    }
                    capacity={maxGuestsDay}
                    amenitiesCount={amenities.length}
                    img={previews[0]}
                  />
                </div>
              </div>

              {submittingVerification && (
                <div className="mt-4">
                  <ProgressBar
                    label="Submitting verification…"
                    value={Math.max(idProgress || 0, rtlProgress || 0)}
                  />
                </div>
              )}

              {saving && (
                <div className="mt-4">
                  <ProgressBar
                    label="Uploading listing…"
                    value={uploadProgress}
                  />
                </div>
              )}

              {!(idStatus === "verified" && rtlStatus === "verified") && (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  You can publish now—your listing will be{" "}
                  <strong>pending</strong> until verification is approved. You can
                  continue verification from Step 1 anytime.
                </div>
              )}

              {/* FIXED: Non-fixed navigation for step 12 to prevent page movement */}
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={back}
                    className="rounded-full border border-neutral-300 px-5 py-2 text-sm hover:border-neutral-400"
                  >
                    Back
                  </button>
                  <button
                    onClick={saveDraftNow}
                    className="rounded-full border border-neutral-300 px-5 py-2 text-sm hover:border-neutral-400"
                  >
                    Save draft
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={publish}
                    disabled={!canPublish || saving}
                    className="rounded-full px-6 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                    style={{ backgroundColor: BRAND }}
                  >
                    {saving ? "Publishing…" : "Publish listing"}
                  </button>
                  <button
                    onClick={exitToHome}
                    className="rounded-full px-5 py-2 text-sm hover:bg-neutral-100"
                  >
                    Home
                  </button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}

/** ============================ UI helpers ============================== **/
function Card({ children }) {
  return (
    <div className="rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
      {children}
    </div>
  );
}
function H2({ children }) {
  return <h2 className="text-lg font-semibold sm:text-xl">{children}</h2>;
}
function AccentBar() {
  return (
    <div
      className="mt-2 h-0.5 w-10 rounded-full"
      style={{ backgroundColor: BRAND }}
    />
  );
}
function Row({ label, value, children }) {
  return (
    <div className="grid gap-1">
      <div className="text-sm text-neutral-500">{label}</div>
      {children ? (
        <div className="text-sm">{children}</div>
      ) : (
        <div className="text-sm">{value}</div>
      )}
    </div>
  );
}
function Field({ label, error, className, children }) {
  return (
    <div className={className}>
      <label className="text-sm">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
// FIXED: WizardNav - removed fixed positioning and spacer
function WizardNav({ onBack, onNext, backDisabled }) {
  return (
    <div className="mt-8 pt-4 border-t border-neutral-200">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          disabled={backDisabled}
          className="rounded-full border border-neutral-300 px-5 py-2 text-sm hover:border-neutral-400 disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="rounded-full px-6 py-2 text-sm font-semibold text-white hover:opacity-90"
          style={{ backgroundColor: BRAND }}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function StatusPill({ status, label }) {
  const map = {
    verified: {
      text: "Verified",
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
    pending: {
      text: "Pending",
      cls: "bg-amber-50 text-amber-800 border-amber-200",
    },
    rejected: {
      text: "Needs attention",
      cls: "bg-red-50 text-red-700 border-red-200",
    },
    unverified: {
      text: "Not verified",
      cls: "bg-neutral-50 text-neutral-700 border-neutral-200",
    },
  };
  const m = map[status] || map.unverified;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${m.cls}`}
    >
      {label ? <strong>{label}:</strong> : null} {m.text}
    </span>
  );
}

function VerificationBadges({ idStatus, rtlStatus }) {
  return (
    <div className="hidden items-center gap-2 sm:flex">
      <StatusPill status={idStatus} label="ID" />
      <StatusPill status={rtlStatus} label="Right to List" />
    </div>
  );
}

function RadioPill({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`rounded-full border px-3.5 py-1.5 text-sm transition ${
        checked
          ? "border-transparent text-white"
          : "border-neutral-300 text-neutral-900 hover:border-neutral-400"
      }`}
      style={checked ? { backgroundColor: BRAND } : {}}
      aria-pressed={checked}
    >
      {label}
    </button>
  );
}

function ToggleChip({ label, checked, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`rounded-full border px-3.5 py-1.5 text-xs transition ${
        checked
          ? "border-transparent text-white"
          : "border-neutral-300 text-neutral-900 hover:border-neutral-400"
      }`}
      style={checked ? { backgroundColor: BRAND } : {}}
      aria-pressed={checked}
    >
      {label}
    </button>
  );
}

function AmenityChip({ name, label, selected, onToggle }) {
  const Icon = ICONS[name] || ICONS._default;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`flex items-center gap-3 rounded-2xl border px-3 py-2 text-left text-sm transition ${
        selected
          ? "border-neutral-300 bg-white"
          : "border-neutral-300 bg-white hover:border-neutral-400"
      }`}
      aria-pressed={selected}
      title={label}
    >
      <Icon selected={selected} />
      <span className={`truncate ${selected ? "font-medium" : ""}`}>{label}</span>
    </button>
  );
}

function UploadCell({
  label,
  file,
  onFile,
  help,
  accept = "image/*,application/pdf",
  capture,
}) {
  const fileInput = useRef(null);
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
  };
  return (
    <div className="rounded-2xl border border-neutral-200 p-3.5">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium">{label}</div>
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:border-neutral-400"
        >
          {file ? "Replace" : "Upload"}
        </button>
      </div>
      {help && <div className="mt-1 text-xs text-neutral-500">{help}</div>}
      <input
        ref={fileInput}
        type="file"
        accept={accept}
        {...(capture ? { capture } : {})}
        className="hidden"
        onChange={onPick}
      />
      {file ? (
        <div className="mt-3">
          {file.type.startsWith("image/") ? (
            <img
              src={URL.createObjectURL(file)}
              alt="preview"
              className="h-28 w-full rounded-xl object-cover"
              onLoad={(e) => URL.revokeObjectURL(e.currentTarget.src)}
            />
          ) : (
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-2 text-xs">
              {file.name} • {(file.size / (1024 * 1024)).toFixed(2)}MB
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 rounded-xl border border-dashed border-neutral-300 p-3 text-center text-xs text-neutral-500">
          No file selected
        </div>
      )}
    </div>
  );
}

function ProgressBar({ label, value }) {
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between text-xs text-neutral-500">
        <span>{label}</span>
        <span>{value}%</span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-neutral-200">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${value}%`, backgroundColor: BRAND }}
        />
      </div>
    </div>
  );
}

function InfoQ({ text }) {
  return (
    <span
      className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-neutral-300 text-[10px] text-neutral-600"
      title={text}
      aria-label="More info"
    >
      ?
    </span>
  );
}

function hourLabel(h) {
  if (h === 24) return "12:00 AM (next day)";
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:00 ${period}`;
}

function TimeSelect({ value, onChange, placeholder, maxHour = 23, refEl }) {
  const val = value === null || value === undefined ? "" : String(value);
  const hours = [];
  for (let h = 0; h <= maxHour; h++) {
    hours.push(
      <option key={h} value={h}>
        {hourLabel(h)}
      </option>
    );
  }
  return (
    <select
      ref={refEl}
      value={val}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-2xl border border-neutral-300 bg-white px-4 py-3 text-sm outline-none focus:border-neutral-400"
    >
      <option value="">{placeholder}</option>
      {hours}
    </select>
  );
}

function LivePreviewCard({
  title,
  city,
  stateCode,
  priceDay,
  priceNight,
  spaceType,
  capacity,
  amenitiesCount,
  img,
}) {
  return (
    <div className="rounded-3xl border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="text-sm font-medium">Listing preview</div>
      <div className="mt-3 overflow-hidden rounded-2xl border border-neutral-200">
        <div className="relative h-40 w-full bg-neutral-100">
          {img ? (
            <img src={img} alt="preview" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-neutral-400">
              Add photos to see your cover image
            </div>
          )}
        </div>
        <div className="p-3">
          <div className="truncate text-sm font-semibold">
            {title || "Untitled listing"}
          </div>
          <div className="mt-0.5 text-xs text-neutral-500">
            {[spaceType, capacity ? `${capacity} guests` : null]
              .filter(Boolean)
              .join(" • ")}
          </div>
          <div className="mt-1 text-xs text-neutral-500">
            {[city, stateCode].filter(Boolean).join(", ")}
          </div>
          <div className="mt-2 flex items-center justify-between">
            <div className="text-sm">
              {priceDay ? <span className="mr-2">{money0(priceDay)}/day</span> : null}
              {priceNight ? <span>{money0(priceNight)}/night</span> : null}
            </div>
            <span className="text-xs text-neutral-500">
              {amenitiesCount} amenities
            </span>
          </div>
          <div className="mt-1 text-[11px] text-neutral-500">
            Display price includes platform fee; taxes added at checkout.
          </div>
        </div>
      </div>
      <div className="mt-3 text-xs text-neutral-500">
        Tip: Clear titles, honest amenities, and bright cover photos drive the best
        bookings.
      </div>
    </div>
  );
}